"use client";
import React, { useState, useEffect } from "react";
import { Button } from "./ui/Button";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import CalculatorModal from "./CalculatorModal";
import { getServiceCodes } from "../apis/getServiceCodes";
import { getSpendingPlans, getSpendingPlanLines } from "../apis/getSpendingPlans";
import { toggleServiceEvv } from "../apis/toggleServiceEvv";
import { getExportableServiceIds } from "../apis/getExportableServiceIds";
import { getConfigurableList } from "../apis/getConfigurableList";
import { addSpendingPlanLine } from "../apis/addSpendingPlansLine";
import { editSpendingPlanLine } from "../apis/editSpendingPlanLine";
import { deleteSpendingPlanLine } from "../apis/deleteSpendingPlanLine";
import { addSpendingPlan } from "../apis/addSpendingPlan";
import { deleteSpendingPlan } from "../apis/deleteSpendingPlan";
import { editSpendingPlan } from "../apis/editSpendingPlan";
import getBusinessList from "../apis/getBusinessList";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import addPatient from "../apis/addPatient";
import login from "../apis/login";
import saveUser from "../apis/saveUser";
import savePatientContact from "../apis/savePatientContact";
import businessLogin from "../apis/businessLogin";
import toast, { Toaster } from 'react-hot-toast';
//const NUMERIC_FIELDS = new Set(["basePay", "pricingUnit", "frequency", "annualCost"]);

// Helper function to format date for API without timezone shift
const formatDateForApi = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function SpendingPlan({ businessId, patientId }) {
   // const sections = initialSections;
   console.log('businessId in ServiceTable:', businessId);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [editingCell, setEditingCell] = useState({ sectionKey: null, rowId: null, field: null });
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("current"); // Add tab state
    // Add state for service codes
    const [serviceCodeOptions, setServiceCodeOptions] = useState([]);
    const [evvServiceIds, setEvvServiceIds] = useState([]); // List of EVV-enabled service IDs
    // Business-configured burden rate options (Description -> Value%), only fetched/shown when a
    // businessId is passed via query param with no patientId — otherwise burden rate is free entry.
    const [burdenRateOptions, setBurdenRateOptions] = useState([]);
    const [spendingPlans, setSpendingPlans] = useState([]);
   // const [planLines, setPlanLines] = useState({});
    const [apiSections, setApiSections] = useState({}); // State for interactive API data
    const [modifiedRows, setModifiedRows] = useState(new Set()); // Track modified rows
    const [savingRows, setSavingRows] = useState(new Set()); // Track rows being saved
    const [approvedBudgets, setApprovedBudgets] = useState({}); // Track approved budgets per service code subtotal
    const [editingPlanDates, setEditingPlanDates] = useState(null); // Track which plan dates are being edited
    const [planDateForms, setPlanDateForms] = useState({}); // Track plan date form values
    const [editingPlanName, setEditingPlanName] = useState(null); // Track which plan name is being edited
    const [planNameForms, setPlanNameForms] = useState({}); // Store name form data for each plan
    const [savingPlanName, setSavingPlanName] = useState(null); // Track which plan name is being saved
    const [savingPlanDates, setSavingPlanDates] = useState(null); // Track which plan dates are being saved
    
    // Service type name management state — customServiceTypes still tracks inline description
    // customizations (see handleApiChange); the modal-based add/duplicate/edit UI that used to
    // live here was removed, see the comment above where handleAddServiceType used to be.
    const [customServiceTypes, setCustomServiceTypes] = useState({}); // Track custom service types with their IDs
    
    // Guest mode and authentication states
    const isGuestMode = !patientId || patientId === '0';
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authMode, setAuthMode] = useState('register'); // 'login' or 'register' - default to register for new users
    const [authForm, setAuthForm] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '', // Add date of birth for prospect registration
        // Emergency contact fields to be sent to save-patient-contact
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        contactMobilePhone: '',
        contactAddress1: '',
        contactCity: '',
        contactState: '',
        contactPostalCode: '',
        contactRelationshipId: 11,
        contactNote: 'Primary emergency contact'
    });
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [isBusinessLoginComplete, setIsBusinessLoginComplete] = useState(false); // Track business login completion
    
    // Plan locking removed — no longer used
    // const [hasBeenPrinted, setHasBeenPrinted] = useState(false);
    
    // Confirmation modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState({
        title: '',
        message: '',
        onConfirm: () => {},
        confirmText: 'Confirm',
        cancelText: 'Cancel'
    });
    
    // Local storage keys for guest mode
    const GUEST_PLANS_KEY = 'guest_spending_plans';
    const GUEST_SECTIONS_KEY = 'guest_api_sections';
    const GUEST_BUDGETS_KEY = 'guest_approved_budgets';
    
    // Helper functions for storage. For guest keys we intentionally DO NOT persist to storage
    // so guest drafts remain only in-memory and are cleared on refresh/new tab.
    const saveToLocalStorage = (key, data) => {
        try {
            // Do not persist guest-scoped keys to any storage (ephemeral only)
            if (typeof key === 'string' && key.toLowerCase().includes('guest')) {
                return;
            }
            localStorage.setItem(key, JSON.stringify(data));
        } catch (error) {
            console.error('Error saving to storage:', error);
        }
    };

    const getFromLocalStorage = (key, defaultValue = []) => {
        try {
            // Guest keys should not be read from persistent storage
            if (typeof key === 'string' && key.toLowerCase().includes('guest')) {
                return defaultValue;
            }
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch (error) {
            console.error('Error reading from storage:', error);
            return defaultValue;
        }
    };
    
    // Plan management states
    const [showAddPlanModal, setShowAddPlanModal] = useState(false);
    const [newPlanForm, setNewPlanForm] = useState({
        planName: '',
        startDate: '',
        endDate: '',
        totalBudget: '',
        description: ''
    });
    
    // Accordion states
    const [expandedPlans, setExpandedPlans] = useState(new Set([0])); // First plan expanded by default
    
    // Loading state for add plan
    const [isAddingPlan, setIsAddingPlan] = useState(false);
    
    // Loading state for data fetching
    const [isLoadingData, setIsLoadingData] = useState(true);
        // Helpers: EVV detection and Live-In check
        const isOriginalServiceEVV = (serviceCode) => {
            try {
                const scStr = String(serviceCode ?? '').trim();

                // 1) Direct id/code/name equality to find the service
                let matchOpt = serviceCodeOptions.find(o => {
                    const candidates = [
                        o?.ServiceTypeID, o?.ServiceTypeId, o?.ID, o?.Id,
                        o?.Name, o?.Code, o?.ServiceCode
                    ].filter(v => v !== undefined && v !== null);
                    return candidates.some(v => String(v).trim() === scStr);
                });

                // 2) Fallback: Name starts with the numeric code (e.g., "315 (EVV) - ...")
                if (!matchOpt) {
                    const escaped = scStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const startsWithCode = new RegExp('^\\s*' + escaped + '\\b');
                    matchOpt = serviceCodeOptions.find(o => typeof o?.Name === 'string' && startsWithCode.test(o.Name));
                }

                // 3) If still not found, try loose compare on ServiceTypeID
                if (!matchOpt) {
                    matchOpt = serviceCodeOptions.find(o => String(o?.ServiceTypeID) === scStr);
                }

                // Check if the service ID is in the EVV-enabled list from API
                if (matchOpt) {
                    const serviceId = matchOpt.ServiceID || matchOpt.ServiceId || matchOpt.ID || matchOpt.Id;
                    // evvServiceIds might be array of objects {serviceId: xxx} or just IDs
                    const isEvv = evvServiceIds.some(evvItem => {
                        const evvId = typeof evvItem === 'object' ? (evvItem.serviceId || evvItem.ServiceId) : evvItem;
                        return String(evvId) === String(serviceId) || evvId === serviceId;
                    });
                    console.log('EVV Check:', {
                        serviceCode: scStr,
                        serviceId,
                        evvServiceIds,
                        isEvv
                    });
                    return isEvv;
                }

                return false;
            } catch (_) { return false; }
        };

        const isLiveInUnchecked = (liveInVal) => {
            if (!liveInVal) return true;
            const s = String(liveInVal).toLowerCase();
            return s === 'false' || s === '0' || s === 'no' || s === 'n';
        };

    // Loading state per plan for lines lazy-loading
    const [planLinesLoading, setPlanLinesLoading] = useState({});
    // const [hasInitialized, setHasInitialized] = useState(false); // Track if data has been loaded once

    // Parse payment multipliers from query parameters or department mapping (runs once on mount)
    useEffect(() => {
        if (businessId === null || businessId === undefined) return;

        const fetchData = async () => {
            try {
                setIsLoadingData(true);

                const urlParams = new URLSearchParams(window.location.search);
                const queryBusinessId = urlParams.get('departmentId') || urlParams.get('businessId');
                const effectiveBusinessId = queryBusinessId || businessId;
                const businessesWithServiceCodes = [10935, 10937, 10942, 11004, 11173, 11215, 9335];
                const shouldPassBusinessId = effectiveBusinessId &&
                    businessesWithServiceCodes.includes(parseInt(effectiveBusinessId));

                // --- Step 1: business login (needed before other authenticated calls) ---
                if (businessId && businessId !== 1 && businessId !== '1') {
                    setIsBusinessLoginComplete(false);
                    try {
                        const loginResponse = await businessLogin(businessId);
                        if (loginResponse.success) {
                            setIsBusinessLoginComplete(true);
                        } else {
                            setIsBusinessLoginComplete(true);
                            if (isGuestMode) toast.error('Business login failed');
                        }
                    } catch (error) {
                        console.error('Business login failed:', error);
                        setIsBusinessLoginComplete(true);
                        if (isGuestMode) toast.error('Failed to authenticate with business. Some features may not work.');
                    }
                } else {
                    localStorage.removeItem('businessAuthToken');
                    localStorage.removeItem('businessUserId');
                    localStorage.removeItem('businessId');
                    setIsBusinessLoginComplete(true);
                }

                // --- Step 2: fire all independent calls in parallel ---
                const [serviceCodesData, evvData, plansData, configListData] = await Promise.all([
                    getServiceCodes(shouldPassBusinessId ? effectiveBusinessId : null).catch(() => ({ data: [] })),
                    effectiveBusinessId
                        ? getExportableServiceIds(effectiveBusinessId).catch(() => ({ data: [] }))
                        : Promise.resolve({ data: [] }),
                    !isGuestMode && patientId
                        ? getSpendingPlans(patientId).catch(() => ({ data: [] }))
                        : Promise.resolve(null),
                    // Only fetch business-configured burden rates when a businessId is passed via
                    // query param (guest embed with businessId only, no patientId) — otherwise
                    // burden rate is a free-entry field and this call is skipped entirely.
                    queryBusinessId
                        ? getConfigurableList(effectiveBusinessId).catch(() => ({ data: [] }))
                        : Promise.resolve({ data: [] }),
                ]);

                // Apply service codes and EVV IDs
                setServiceCodeOptions(serviceCodesData?.data || []);
                setEvvServiceIds(evvData?.data || []);

                // Business-configured burden rate options (e.g. "Direct"/"Indirect" -> Value%)
                if (queryBusinessId) {
                    const options = (configListData?.data || [])
                        .filter(item => item.TableID === 'CustomBillCodeBasedOnPayCode')
                        .map(item => ({ label: item.Description, value: parseFloat(item.Value) || 0 }))
                        .filter(opt => opt.label);
                    setBurdenRateOptions(options);
                } else {
                    setBurdenRateOptions([]);
                }

                // --- Step 3: handle guest vs authenticated ---
                if (isGuestMode) {
                    const guestPlans = getFromLocalStorage(GUEST_PLANS_KEY, []);
                    const guestSections = getFromLocalStorage(GUEST_SECTIONS_KEY, {});
                    const guestBudgets = getFromLocalStorage(GUEST_BUDGETS_KEY, {});
                    setSpendingPlans(guestPlans);
                    setApiSections(guestSections);
                    setApprovedBudgets(guestBudgets);

                    // Default to whichever tab actually has a plan to show — if there's no
                    // current plan but there is a future one, open Future Plans by default
                    // instead of landing on an empty "No Current Plans" state.
                    if (guestPlans.length > 0) {
                        if (getCurrentPlans(guestPlans).length > 0) {
                            setActiveTab('current');
                        } else if (getFuturePlans(guestPlans).length > 0) {
                            setActiveTab('future');
                        } else {
                            setActiveTab('previous');
                        }
                        setExpandedPlans(new Set([0]));
                    } else {
                        setExpandedPlans(new Set());
                    }
                } else {
                    const plans = plansData?.data || [];
                    setSpendingPlans(plans);

                    // Default to whichever tab actually has a plan to show — if there's no
                    // current plan but there is a future one, open Future Plans by default
                    // instead of landing on an empty "No Current Plans" state.
                    if (plans.length > 0) {
                        if (getCurrentPlans(plans).length > 0) {
                            setActiveTab('current');
                        } else if (getFuturePlans(plans).length > 0) {
                            setActiveTab('future');
                        } else {
                            setActiveTab('previous');
                        }
                    }

                    if (plans.length > 0) {
                        // --- Step 4: fetch all plan lines in parallel ---
                        const allLines = await Promise.all(
                            plans.map(async (plan) => {
                                const linesData = await getSpendingPlanLines(plan.PlanId);
                                return { planId: plan.PlanId, lines: linesData?.data || [] };
                            })
                        );

                        const apiSectionsMap = {};
                        allLines.forEach(({ planId, lines }) => {
                            const groupedByPlanType = lines.reduce((acc, line) => {
                                const planType = line.plan_type || 'Other';
                                const sectionKey = planType.toLowerCase().replace(/\s+/g, '').replace(/[^a-z]/g, '');
                                if (!acc[sectionKey]) acc[sectionKey] = [];

                                const basePay = parseFloat(line.BasePay) || 0;
                                const burdenRate = parseFloat(line.PaymentModel) || 0;
                                const correctPricingUnit = basePay + (basePay * burdenRate / 100);
                                const allocatedBudgetValue = (line.AllocatedBudget !== null && line.AllocatedBudget !== undefined) ? parseFloat(line.AllocatedBudget) : null;

                                acc[sectionKey].push({
                                    id: line.LineId,
                                    serviceCode: line.ServiceCode || '',
                                    provider: line.Provider || line.ProviderName || '',
                                    liveIn: line.LiveIn || false,
                                    description: line.ServiceDescription || line.Description || '',
                                    ippGoal: line.IPPGoal || '',
                                    basePay: basePay,
                                    burdenRate: burdenRate,
                                    pricingUnit: correctPricingUnit,
                                    frequency: parseInt(line.AnnualFrequency) || 0,
                                    annualCost: parseFloat(line.TotalAnnualCost) || 0,
                                    allocatedBudget: allocatedBudgetValue,
                                    customServiceId: line.CustomServiceId || line.ServiceId || line.ServiceID || null,
                                    isApi: true,
                                    planId: planId,
                                    originalLine: line
                                });
                                return acc;
                            }, {});
                            apiSectionsMap[planId] = groupedByPlanType;
                        });

                        setApiSections(apiSectionsMap);
                        setExpandedPlans(new Set([0]));
                    } else {
                        setApiSections({});
                        setExpandedPlans(new Set());
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchData();
    }, [patientId, businessId, isGuestMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load custom service types from localStorage
    useEffect(() => {
        try {
            const stored = localStorage.getItem('customServiceTypes');
            if (stored) {
                setCustomServiceTypes(JSON.parse(stored));
            }
        } catch (error) {
            console.error('Error loading custom service types:', error);
        }
    }, []);

    // Save custom service types to localStorage whenever they change (but not for guest mode keys)
    useEffect(() => {
        try {
            if (Object.keys(customServiceTypes).length > 0) {
                localStorage.setItem('customServiceTypes', JSON.stringify(customServiceTypes));
            }
        } catch (error) {
            console.error('Error saving custom service types:', error);
        }
    }, [customServiceTypes]);

    /** ---- HELPERS ---- **/
    // const handleAddRow = (sectionKey) => {
    //     const newRow = {
    //         id: sections[sectionKey].length + 1,
    //         serviceCode: "",
    //         provider: "",
    //         description: "",
    //         ippGoal: "",
    //         basePay: "",
    //         baseBasePay: 0, // keep raw values
    //         paymentModel: "",
    //         pricingUnit: "",
    //         basePricingUnit: 0, // keep raw values
    //         frequency: "",
    //         annualCost: "",
    //         isNew: true,
    //     };
    //     setSections({ ...sections, [sectionKey]: [...sections[sectionKey], newRow] });
    // };

    // Add copy row handler
    // const handleCopyRow = (sectionKey, row) => {
    //     const newRow = {
    //         ...row,
    //         id: sections[sectionKey].length + 1, // new ID
    //         isNew: true, // mark as new until saved
    //         baseBasePay: row.baseBasePay ?? row.basePay, // keep original
    //         basePricingUnit: row.basePricingUnit ?? row.pricingUnit,
    //     };
    //     setSections((prev) => ({
    //         ...prev,
    //         [sectionKey]: [...prev[sectionKey], newRow],
    //     }));
    // };

    // const handleChange = (sectionKey, id, field, value) => {
    //     setSections((prev) => ({
    //         ...prev,
    //         [sectionKey]: prev[sectionKey].map((r) => {
    //             if (r.id !== id) return r;
    //             const updatedRow = { ...r };

    //             // Normalize numeric inputs
    //             const v =
    //                 NUMERIC_FIELDS.has(field) ? (value === "" ? "" : Number(value)) : value;

    //             // Update editable fields
    //             if (field === "basePay") {
    //                 updatedRow.basePay = v;
    //             } else if (field === "paymentModel") {
    //                 updatedRow.paymentModel = v;
    //             } else if (field === "frequency") {
    //                 updatedRow.frequency = v; // make sure freq is updated immediately
    //             } else {
    //                 updatedRow[field] = v;
    //             }

    //             // Multiplier mapping per dropdown
    //             let multiplier = 0;
    //             if (updatedRow.paymentModel === "Sole-Employer") multiplier = 11.5;
    //             if (updatedRow.paymentModel === "Co-Employer") multiplier = 20.5;
    //             if (updatedRow.paymentModel === "Bill Payer") multiplier = 0;

    //             const base = Number(updatedRow.basePay) || 0;

    //             // Pricing unit = basePay + (basePay * percentage/100)
    //             updatedRow.pricingUnit = base + (base * multiplier / 100);

    //             // --- recalculate frequency if pricingUnit changes and not editing frequency directly ---
    //             if ((field === "basePay" || field === "paymentModel") && updatedRow.pricingUnit) {
    //                 updatedRow.frequency = Number((updatedRow.pricingUnit * 12).toFixed(2));
    //             }

    //             const freq =
    //                 field === "frequency"
    //                     ? Number(v) || 0
    //                     : Number(updatedRow.frequency) || 0;
    //             const pu = Number(updatedRow.pricingUnit) || 0;

    //             // Annual cost = frequency × pricing unit
    //             updatedRow.annualCost = freq * pu;

    //             return updatedRow;
    //         }),
    //     }));
    // };

    // const handleSave = (sectionKey, id) => {
    //     setSections({
    //         ...sections,
    //         [sectionKey]: sections[sectionKey].map((r) =>
    //             r.id === id ? { ...r, isNew: false } : r
    //         ),
    //     });
    // };

    // const handleDelete = (sectionKey, id) => {
    //     setSections({
    //         ...sections,
    //         [sectionKey]: sections[sectionKey].filter((r) => r.id !== id),
    //     });
    // };

    // API data handlers
    const handleApiCopyRow = (planId, sectionKey, row) => {
        // Use functional updater so we derive a consistent new id from the latest state
        setApiSections(prev => {
            const currentRows = prev?.[planId]?.[sectionKey] || [];
            const newId = Math.max(...currentRows.map(r => r.id), 0) + 1;
            const newRow = { ...row, id: newId, isNew: true };

            const updated = {
                ...prev,
                [planId]: {
                    ...prev[planId],
                    [sectionKey]: [...currentRows, newRow]
                }
            };

            // Persist for guest users
            if (isGuestMode) saveToLocalStorage(GUEST_SECTIONS_KEY, updated);

            // Mark the new row as modified so the UI shows save state
            setModifiedRows(prevSet => {
                const s = new Set(prevSet);
                s.add(`${planId}-${sectionKey}-${newId}`);
                return s;
            });

            // Focus the new row's serviceCode field for immediate editing
            setEditingCell({ sectionKey: `${planId}-${sectionKey}`, rowId: newId, field: 'serviceCode' });

            return updated;
        });
    };

    const handleApiDeleteRow = async (planId, sectionKey, row) => {
        console.log('Deleting row:', { planId, sectionKey, row });
        
        // Plan lock removed — allow deletion in guest mode
        
        // Show confirmation modal before deleting
        setConfirmModalData({
            title: 'Delete Row',
            message: `Are you sure you want to delete this row?\n\nThis action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setShowConfirmModal(false);
                await performDeleteRow(planId, sectionKey, row);
            }
        });
        setShowConfirmModal(true);
    };

    const performDeleteRow = async (planId, sectionKey, row) => {
        try {
            if (isGuestMode) {
                // Guest mode - remove from localStorage
                const updatedSections = {
                    ...apiSections,
                    [planId]: {
                        ...apiSections[planId],
                        [sectionKey]: apiSections[planId][sectionKey].filter(r => r.id !== row.id)
                    }
                };
                setApiSections(updatedSections);
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
                
                // Remove from modified rows if it was there
                setModifiedRows(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(`${planId}-${sectionKey}-${row.id}`);
                    return newSet;
                });
                
                return;
            }

            // Authenticated mode - delete from API
            if (!row.originalLine?.LineId && !row.isNew) {
                console.warn('No LineId found for deletion');
                return;
            }

            // Only call API if row exists in backend (has LineId)
            if (row.originalLine?.LineId) {
                await deleteSpendingPlanLine({ LineId: row.originalLine.LineId });
            }
            
            // Remove from local state
            setApiSections(prev => ({
                ...prev,
                [planId]: {
                    ...prev[planId],
                    [sectionKey]: prev[planId][sectionKey].filter(r => r.id !== row.id)
                }
            }));
            
            // Remove from modified rows if it was there
            setModifiedRows(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${planId}-${sectionKey}-${row.id}`);
                return newSet;
            });
            
            // Refresh data if it was an existing row
            if (row.originalLine?.LineId) {
                await refreshPlanData();
            }
        } catch (error) {
            console.error('Error deleting spending plan line:', error);
            toast.error('Failed to delete spending plan line. Please try again.');
        }
    };

    const handleApiAddRow = (planId, sectionKey) => {
        console.log('Adding new row for plan:', planId, 'section:', sectionKey);
        
        // Plan lock removed — allow adding rows in guest mode

        // Create row using functional updater to avoid id collisions when adding quickly
        setApiSections(prev => {
            const currentRows = prev?.[planId]?.[sectionKey] || [];
            const newId = Math.max(...currentRows.map(r => r.id), 0) + 1;

            const basePay = 0;
            const pricingUnit = basePay; // No burden rate applied yet, so pricing unit equals base pay
            const frequency = 1; // Default frequency is 1 (yearly)
            const annualCost = pricingUnit * frequency; // Calculate annual cost

            const newRow = {
                id: newId,
                serviceCode: '',
                provider: '',
                liveIn: false,
                description: '',
                ippGoal: '',
                basePay: basePay,
                burdenRate: 0,
                pricingUnit: pricingUnit,
                frequency: frequency,
                annualCost: annualCost,
                isApi: true,
                planId: planId,
                isNew: true
            };

            const updatedSections = {
                ...prev,
                [planId]: {
                    ...prev[planId],
                    [sectionKey]: [...(prev[planId]?.[sectionKey] || []), newRow]
                }
            };

            // Save to localStorage in guest mode
            if (isGuestMode) {
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
            }

            // Mark as modified using the new id
            const rowKey = `${planId}-${sectionKey}-${newId}`;
            setModifiedRows(prevSet => {
                const s = new Set(prevSet);
                s.add(rowKey);
                return s;
            });

            // Auto-focus the serviceCode field for the new row (schedule after state update)
            setTimeout(() => {
                setEditingCell({ sectionKey: `${planId}-${sectionKey}`, rowId: newId, field: 'serviceCode' });
            }, 0);

            return updatedSections;
        });
    };

    const handleApiSave = async (planId, sectionKey, row) => {
        console.log('Saving row:', { planId, sectionKey, row });
        
        // Plan lock removed — allow saving in guest mode

        const rowKey = `${planId}-${sectionKey}-${row.id}`;
        setSavingRows(prev => new Set([...prev, rowKey]));
        
        try {
            if (isGuestMode) {
                // Guest mode - save to localStorage
                const updatedSections = {
                    ...apiSections,
                    [planId]: {
                        ...apiSections[planId],
                        [sectionKey]: apiSections[planId][sectionKey].map(r => 
                            r.id === row.id ? { ...row, isNew: false } : r
                        )
                    }
                };
                setApiSections(updatedSections);
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
                
                // Remove from modified rows
                setModifiedRows(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(rowKey);
                    return newSet;
                });
                
                return;
            }

            // Authenticated mode - save to API
            // Map section key to plan_type
            const planTypeMap = {
                'livingarrangements': 'Living Arrangements',
                'employmentandcommunityparticipation': 'Employment and Community Participation', 
                'healthandsafety': 'Health and Safety'
            };
            
            // Try to include any custom service id so backend can persist it in the line
            const resolveCustomServiceId = () => {
                // 1) From the row itself
                if (row.customServiceId) return row.customServiceId;
                // 2) From our in-memory mapping using this row key
                const key = `${planId}-${sectionKey}-${row.id}`;
                const mapped = customServiceTypes[key]?.serviceId;
                if (mapped) return mapped;
                // 3) Heuristic: find by same serviceCode and description within the same plan/section mapping
                const entries = Object.values(customServiceTypes || {});
                const match = entries.find(v => v && String(v.serviceCode) === String(row.serviceCode) && (v.customName === row.description || v.originalServiceDescription === row.description));
                return match?.serviceId || null;
            };
            const customServiceId = resolveCustomServiceId();

            const payload = {
                PlanId: planId,
                ServiceCode: row.serviceCode || '',
                Provider: row.provider || '',
                LiveIn: row.liveIn || false,
                ServiceDescription: row.description || row.serviceCode || '',
                IPPGoal: row.ippGoal || '',
                BasePay: Number(row.basePay) || 0,
                PricingUnit: Number(row.pricingUnit) || 0,
                AnnualFrequency: Number(row.frequency) || 0,
                TotalAnnualCost: Number(row.pricingUnit) * Number(row.frequency) || 0,
                AllocatedBudget: (row.allocatedBudget !== null && row.allocatedBudget !== undefined && row.allocatedBudget !== '') ? Number(row.allocatedBudget) : null,
                plan_type: planTypeMap[sectionKey] || 'Other',
                PaymentModel: String(row.burdenRate ?? 0),
                // New: Persist custom service name id along with the line
                CustomServiceId: customServiceId,
                // Ensure business id is explicitly sent to backend (both cases for compatibility)
                BusinessId: businessId,
            };
            // Attach CreatedByUserId when available from demo credentials or temporary window var
            try {
                const stored = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                const createdBy = stored?.userId || window.__createdUserId || null;
                if (createdBy) payload.CreatedByUserId = createdBy;
            } catch (e) { /* ignore */ }
            
            if (row.isNew) {
                // Add new row
                await addSpendingPlanLine(payload);
            } else if (row.originalLine?.LineId) {
                // Edit existing row
                payload.LineId = row.originalLine.LineId;
                await editSpendingPlanLine(payload);
            }
            
            // If original service is EVV and live-in is unchecked, toggle EVV on the overridden service
            try {
                const customKey = `${planId}-${sectionKey}-${row.id}`;
                const newServiceId = row.customServiceId || customServiceTypes[customKey]?.serviceId || null;
                const shouldToggle = !isGuestMode && isOriginalServiceEVV(row.serviceCode) && isLiveInUnchecked(row.liveIn) && newServiceId;
                console.log('EVV toggle check:', {
                    guestMode: isGuestMode,
                    originalServiceCode: row.serviceCode,
                    isEVV: isOriginalServiceEVV(row.serviceCode),
                    liveInUnchecked: isLiveInUnchecked(row.liveIn),
                    newServiceId
                });
                if (shouldToggle) {
                    const resp = await toggleServiceEvv(newServiceId, 1);
                    console.log('toggleServiceEvv response:', resp);
                }
            } catch (e) {
                console.warn('toggleServiceEvv call failed (non-blocking):', e);
            }

            // Remove from modified rows
            setModifiedRows(prev => {
                const newSet = new Set(prev);
                newSet.delete(rowKey);
                return newSet;
            });
            
            // Refresh data to get updated information
            await refreshPlanData();
            
        } catch (error) {
            console.error('Error saving spending plan line:', error);
            toast.error('Failed to save spending plan line. Please try again.');
        } finally {
            setSavingRows(prev => {
                const newSet = new Set(prev);
                newSet.delete(rowKey);
                return newSet;
            });
        }
    };

    // Function to handle saving plan date changes
    const handleSavePlanDates = async (planId) => {
        try {
            setSavingPlanDates(planId);
            const dateForm = planDateForms[planId];
            if (!dateForm) {
                setSavingPlanDates(null);
                return;
            }

            // Find the current plan to get all its properties
            const currentPlan = spendingPlans.find(plan => plan.PlanId === planId);
            if (!currentPlan) {
                toast.error('Plan not found');
                setSavingPlanDates(null);
                return;
            }

            // Format dates to avoid timezone shift
            const formattedStartDate = formatDateForApi(dateForm.startDate);
            const formattedEndDate = formatDateForApi(dateForm.endDate);

            // Send the full plan object with updated dates
            const payload = {
                ...currentPlan,
                Name: currentPlan.Name || currentPlan.planName || '',
                start_date: formattedStartDate,
                end_date: formattedEndDate,
                StartDate: formattedStartDate, // Include both formats in case API expects different property names
                EndDate: formattedEndDate,
                BusinessId: businessId,
                businessId: businessId
            };

            await editSpendingPlan(payload);

            // Update the local state with new dates
            setSpendingPlans(prev => prev.map(plan =>
                plan.PlanId === planId
                    ? { ...plan, start_date: formattedStartDate, end_date: formattedEndDate }
                    : plan
            ));

            // Clear editing state
            setEditingPlanDates(null);
            setPlanDateForms(prev => {
                const newForms = { ...prev };
                delete newForms[planId];
                return newForms;
            });

            toast.success('Plan dates updated successfully!');
        } catch (error) {
            console.error('Error updating plan dates:', error);
            toast.error('Failed to update plan dates. Please try again.');
        } finally {
            setSavingPlanDates(null);
        }
    };

    const handleSavePlanName = async (planId) => {
        try {
            setSavingPlanName(planId);
            const nameForm = planNameForms[planId];
            const currentPlan = spendingPlans.find(p => p.PlanId === planId);
            
            if (!nameForm || !currentPlan) {
                toast.error('Invalid plan or name data');
                setSavingPlanName(null);
                return;
            }

            // Send the plan object with updated name
            const payload = {
                ...currentPlan,
                Name: nameForm.planName,
                StartDate: currentPlan.start_date || currentPlan.StartDate,
                EndDate: currentPlan.end_date || currentPlan.EndDate,
                BusinessId: businessId,
                businessId: businessId
            };

            await editSpendingPlan(payload);
            
            // Update the local state with new name
            setSpendingPlans(prev => prev.map(plan => 
                plan.PlanId === planId 
                    ? { ...plan, Name: nameForm.planName }
                    : plan
            ));

            // Clear editing state
            setEditingPlanName(null);
            setPlanNameForms(prev => {
                const newForms = { ...prev };
                delete newForms[planId];
                return newForms;
            });

            toast.success('Plan name updated successfully!');
        } catch (error) {
            console.error('Error updating plan name:', error);
            toast.error('Failed to update plan name. Please try again.');
        } finally {
            setSavingPlanName(null);
        }
    };

    const refreshPlanData = async () => {
        // Only refresh in authenticated mode
        if (isGuestMode) {
            console.log('Skipping refresh in guest mode');
            return;
        }

        try {
            console.log('Refreshing plan data for authenticated user...');
            const plans = spendingPlans;
            const linesPromises = plans.map(async (plan) => {
                const linesData = await getSpendingPlanLines(plan.PlanId);
                return { planId: plan.PlanId, lines: linesData?.data || [] };
            });

            const allLines = await Promise.all(linesPromises);
            const linesMap = {};
            const apiSectionsMap = {};
            
            allLines.forEach(({ planId, lines }) => {
                linesMap[planId] = lines;
                
                const groupedByPlanType = lines.reduce((acc, line) => {
                    const planType = line.plan_type || 'Other';
                    const sectionKey = planType.toLowerCase().replace(/\s+/g, '').replace(/[^a-z]/g, '');
                    
                    if (!acc[sectionKey]) acc[sectionKey] = [];
                    
                    // Calculate correct pricing unit based on burden rate
                    const basePay = parseFloat(line.BasePay) || 0;
                    const burdenRate = parseFloat(line.PaymentModel) || 0;
                    const correctPricingUnit = basePay + (basePay * burdenRate / 100);

                    const allocatedBudgetValue = (line.AllocatedBudget !== null && line.AllocatedBudget !== undefined) ? parseFloat(line.AllocatedBudget) : null;

                    acc[sectionKey].push({
                        id: line.LineId,
                        serviceCode: line.ServiceCode || '',
                        provider: line.Provider || line.ProviderName || '',
                        liveIn: line.LiveIn || false,
                        description: line.ServiceDescription || line.Description || '',
                        ippGoal: line.IPPGoal || '',
                        basePay: basePay,
                        burdenRate: burdenRate,
                        pricingUnit: correctPricingUnit,
                        frequency: parseInt(line.AnnualFrequency) || 0,
                        annualCost: parseFloat(line.TotalAnnualCost) || 0,
                        allocatedBudget: allocatedBudgetValue,
                        customServiceId: line.CustomServiceId || line.ServiceId || line.ServiceID || null,
                        isApi: true,
                        planId: planId,
                        originalLine: line,
                        isNew: false
                    });
                    
                    return acc;
                }, {});
                
                apiSectionsMap[planId] = groupedByPlanType;
            });
            
           // setPlanLines(linesMap);
            setApiSections(apiSectionsMap);
        } catch (error) {
            console.error('Error refreshing plan data:', error);
        }
    };

    const handleApiChange = (planId, sectionKey, rowId, field, value) => {
        // Plan lock removed — allow changes in guest mode

        const rowKey = `${planId}-${sectionKey}-${rowId}`;
        
        const updatedSections = {
            ...apiSections,
            [planId]: {
                ...apiSections[planId],
                [sectionKey]: apiSections[planId][sectionKey].map(row => {
                    if (row.id === rowId) {
                        const updatedRow = { ...row, [field]: value };
                        
                        // Recalculate pricing unit when the burden rate changes
                        if (field === "burdenRate") {
                            const burdenRate = Number(value) || 0;
                            updatedRow.pricingUnit = Number(updatedRow.basePay) + (Number(updatedRow.basePay) * burdenRate / 100);
                            // Keep existing frequency, just update annual cost
                            updatedRow.annualCost = updatedRow.pricingUnit * (updatedRow.frequency || 0);
                        }

                        // Recalculate pricing unit when base pay changes (for current burden rate)
                        if (field === "basePay") {
                            const burdenRate = Number(updatedRow.burdenRate) || 0;
                            updatedRow.pricingUnit = Number(value) + (Number(value) * burdenRate / 100);
                            // Keep existing frequency, just update annual cost
                            updatedRow.annualCost = updatedRow.pricingUnit * (updatedRow.frequency || 0);
                        }
                        
                        // When frequency changes, update annual cost
                        if (field === "frequency") {
                            // Allow empty string while editing so backspace can clear the field
                            if (value === '') {
                                updatedRow.frequency = '';
                                updatedRow.annualCost = (Number(updatedRow.pricingUnit) || 0) * 0;
                            } else {
                                const freq = Number(value) || 0;
                                const pu = Number(updatedRow.pricingUnit) || 0;
                                updatedRow.frequency = freq;
                                // Annual cost is automatically calculated as pricing unit × frequency
                                updatedRow.annualCost = freq * pu;
                            }
                        }
                        
                        return updatedRow;
                    }
                    return row;
                })
            }
        };

        setApiSections(updatedSections);
        
        // Save to localStorage in guest mode
        if (isGuestMode) {
            saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
        }
        
        // Mark row as modified
        console.log('Marking row as modified:', rowKey);
        setModifiedRows(prev => {
            const newSet = new Set([...prev, rowKey]);
            console.log('Modified rows after change:', Array.from(newSet));
            return newSet;
        });
        
        // Track when description is manually changed to mark as custom
        if (field === 'description') {
            const customKey = `${planId}-${sectionKey}-${rowId}`;
            // Find original service description to compare
            const row = apiSections[planId]?.[sectionKey]?.find(r => r.id === rowId);
            if (row?.serviceCode) {
                const serviceCodeOption = serviceCodeOptions.find(opt => 
                    (opt.ServiceTypeID === row.serviceCode || opt.Name === row.serviceCode)
                );
                const originalServiceDescription = serviceCodeOption?.Description || serviceCodeOption?.ServiceDescription || serviceCodeOption?.Name || row.serviceCode || '';
                
                // If description is different from original, mark as manually customized
                if (value && value !== originalServiceDescription) {
                    const existingCustomType = customServiceTypes[customKey];
                    setCustomServiceTypes(prev => {
                        const updated = {
                            ...prev,
                            [customKey]: {
                                serviceId: existingCustomType?.serviceId || null, // Preserve existing ServiceID if any
                                originalServiceDescription: originalServiceDescription,
                                customName: value,
                                serviceCode: row.serviceCode,
                                isCustomized: true,
                                manuallyEdited: true
                            }
                        };
                        // Immediately save to localStorage
                        try {
                            localStorage.setItem('customServiceTypes', JSON.stringify(updated));
                        } catch (error) {
                            console.error('Error saving to localStorage:', error);
                        }
                        return updated;
                    });
                } else if (value === originalServiceDescription && customServiceTypes[customKey]) {
                    // If description is changed back to original, remove the customization
                    setCustomServiceTypes(prev => {
                        const updated = { ...prev };
                        delete updated[customKey];
                        // Immediately save to localStorage
                        try {
                            localStorage.setItem('customServiceTypes', JSON.stringify(updated));
                        } catch (error) {
                            console.error('Error saving to localStorage:', error);
                        }
                        return updated;
                    });
                }
            }
        }
    };

    // Add/Duplicate/Edit Custom Service Type Name (modal-based flow) was removed here — that
    // capability is intentionally exclusive to the embedded (V2) version for now. Inline
    // description editing (handleApiChange, above) still works and still tracks customization
    // via customServiceTypes.

    // const calcSubTotal = (rows) =>
    //     rows.reduce(
    //         (sum, r) => sum + (Number(r.frequency || 0) * Number(r.pricingUnit || 0)),
    //         0
    //     );

    // const calcGrandTotal = () =>
    //     calcSubTotal(sections.livingArrangements) + calcSubTotal(sections.healthAndSafety);

    // Get plans based on date ranges
    const getCurrentPlans = (plans = spendingPlans) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day
        return plans.filter(plan => {
            const startDate = new Date(plan.start_date);
            startDate.setHours(0, 0, 0, 0); // Set to start of day
            const endDate = new Date(plan.end_date);
            endDate.setHours(23, 59, 59, 999); // Set to end of day
            return today >= startDate && today <= endDate;
        });
    };

    const getPreviousPlans = (plans = spendingPlans) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day
        return plans.filter(plan => {
            const endDate = new Date(plan.end_date);
            endDate.setHours(23, 59, 59, 999); // Set to end of day
            return endDate < today;
        });
    };

    // Parse date string to proper Date object, handling various formats
    const parseDate = (dateInput) => {
        if (!dateInput) return null;
        if (dateInput instanceof Date) return dateInput;
        
        // Handle ISO format (YYYY-MM-DD)
        if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}/)) {
            return new Date(dateInput);
        }
        
        // Handle DD/MM/YYYY format
        if (typeof dateInput === 'string' && dateInput.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
            const [day, month, year] = dateInput.split('/');
            return new Date(year, month - 1, day); // month is 0-indexed
        }
        
        // Handle MM/DD/YYYY format  
        if (typeof dateInput === 'string' && dateInput.includes('/')) {
            // Try parsing as MM/DD/YYYY first, then DD/MM/YYYY
            const parts = dateInput.split('/');
            if (parts.length === 3) {
                const [first, second, year] = parts;
                // If first part > 12, assume DD/MM/YYYY, otherwise MM/DD/YYYY
                if (parseInt(first) > 12) {
                    return new Date(year, second - 1, first); // DD/MM/YYYY
                } else {
                    return new Date(year, first - 1, second); // MM/DD/YYYY
                }
            }
        }
        
        // Fallback to standard Date parsing
        return new Date(dateInput);
    };

    // Convert date to ISO format (YYYY-MM-DD) for HTML date inputs
    // const dateToISO = (dateInput) => {
    //     if (!dateInput) return '';
    //     const date = parseDate(dateInput);
    //     if (!date || isNaN(date)) return '';
    //     return date.toISOString().split('T')[0];
    // };

    // Format a date for US display (MM/DD/YYYY)
    const formatDateUS = (dateInput) => {
        if (!dateInput) return '';
        const d = parseDate(dateInput);
        if (!d || isNaN(d)) return '';
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
    };

    // Parse a US date string (MM/DD/YYYY or M/D/YYYY) into ISO (YYYY-MM-DD) for storage
    // const parseDateUSToISO = (str) => {
    //     if (!str) return '';
    //     // Accept separators slash or hyphen
    //     const s = String(str).trim();
    //     const parts = s.includes('/') ? s.split('/') : s.split('-');
    //     if (parts.length !== 3) return '';
    //     let [m, d, y] = parts.map(p => p.trim());
    //     // If year is two digits, try to expand (assume 20xx)
    //     if (y.length === 2) y = `20${y}`;
    //     const mm = m.padStart(2, '0');
    //     const dd = d.padStart(2, '0');
    //     const iso = `${y}-${mm}-${dd}`;
    //     const dt = new Date(iso);
    //     if (isNaN(dt)) return '';
    //     return iso;
    // };

    // Use native date inputs (ISO strings) for calendar picker while formatting for display elsewhere

    const getFuturePlans = (plans = spendingPlans) => {
        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of day
        return plans.filter(plan => {
            const startDate = new Date(plan.start_date);
            startDate.setHours(0, 0, 0, 0); // Set to start of day
            return startDate > today;
        });
    };

    // Switch to whichever tab a plan actually belongs in (Previous/Current/Future) and expand
    // it there — used right after adding a plan so it's immediately visible, since the plan's
    // date range may not match whatever tab happens to be active at the time.
    const switchToPlanTab = (plan, plansIncludingNew) => {
        let tab, filtered;
        if (getFuturePlans([plan]).length > 0) {
            tab = 'future';
            filtered = getFuturePlans(plansIncludingNew);
        } else if (getPreviousPlans([plan]).length > 0) {
            tab = 'previous';
            filtered = getPreviousPlans(plansIncludingNew);
        } else {
            tab = 'current';
            filtered = getCurrentPlans(plansIncludingNew);
        }
        setActiveTab(tab);
        const index = filtered.findIndex(p => p.PlanId === plan.PlanId);
        if (index >= 0) {
            setExpandedPlans(new Set([index]));
        }
    };

    // Plan management functions
    const handleAddPlan = async () => {
        if (isAddingPlan) return; // Prevent multiple calls
        
        try {
            setIsAddingPlan(true); // Set loading state
            
            if (isGuestMode) {
                // Guest mode - save to localStorage
                const newPlan = {
                    PlanId: Date.now(), // Use timestamp as unique ID for guest plans
                    Name: newPlanForm.planName || '',
                    //planName: newPlanForm.planName || '',
                    Year: new Date(newPlanForm.startDate).getFullYear() || new Date().getFullYear(),
                    IsActive: 1,
                    start_date: formatDateForApi(newPlanForm.startDate),
                    end_date: formatDateForApi(newPlanForm.endDate),
                    created_at: new Date().toISOString(),
                    isGuestPlan: true
                };
                
                console.log('Adding new guest plan:', newPlan);
                
                // Update local state and localStorage
                const updatedPlans = [...spendingPlans, newPlan];
                setSpendingPlans(updatedPlans);
                // Switch to whichever tab (Previous/Current/Future) this plan's date range
                // actually belongs to, and open it there
                switchToPlanTab(newPlan, updatedPlans);
                saveToLocalStorage(GUEST_PLANS_KEY, updatedPlans);
                
                // Initialize empty sections for the new plan
                const updatedSections = { 
                    ...apiSections, 
                    [newPlan.PlanId]: {
                        'livingarrangements': [],
                        'employmentandcommunityparticipation': [],
                        'healthandsafety': []
                    }
                };
                setApiSections(updatedSections);
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
                
                toast.success('Spending plan created successfully!');
            } else {
                // Authenticated mode - use API
                const payload = {
                    PatientId: patientId,
                    Name: newPlanForm.planName || '',
                    Year: new Date(newPlanForm.startDate).getFullYear() || new Date().getFullYear(),
                    IsActive: 1,
                    StartDate: formatDateForApi(newPlanForm.startDate),
                    EndDate: formatDateForApi(newPlanForm.endDate),
                    BusinessId: businessId,
                    businessId: businessId
                };

                // Attach CreatedByUserId when available from demo credentials or temporary window var
                try {
                    const stored = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                    const createdBy = stored?.userId || window.__createdUserId || null;
                    if (createdBy) payload.CreatedByUserId = createdBy;
                } catch (e) {
                    // ignore
                }
                
                console.log('Adding new plan with payload:', payload);
                
                // Call add API endpoint
                const result = await addSpendingPlan(payload);
                console.log('Plan added successfully:', result);
                
                // Refresh spending plans after successful addition
                const plansData = await getSpendingPlans(patientId);
                const updatedPlans = plansData?.data || [];
                setSpendingPlans(updatedPlans);

                // Switch to whichever tab (Previous/Current/Future) this plan's date range
                // actually belongs to, and open it there. Match by the dates we just submitted
                // since the refreshed list doesn't carry our client-side PlanId reference.
                const newlyAddedPlan = [...updatedPlans].reverse().find(p =>
                    formatDateForApi(p.start_date) === payload.StartDate && formatDateForApi(p.end_date) === payload.EndDate
                ) || updatedPlans[updatedPlans.length - 1];
                if (newlyAddedPlan) {
                    switchToPlanTab(newlyAddedPlan, updatedPlans);
                }

                toast.success('Spending plan added successfully!');
            }
            
            // Close modal and reset form
            setShowAddPlanModal(false);
            setNewPlanForm({
                planName: '',
                startDate: '',
                endDate: '',
                totalBudget: '',
                description: ''
            });
            
        } catch (error) {
            console.error('Error adding plan:', error);
            toast.error('Failed to add spending plan. Please try again.');
        } finally {
            setIsAddingPlan(false); // Reset loading state
        }
    };

    const handleDeletePlan = async (planId, planName) => {
        setConfirmModalData({
            title: 'Delete Plan',
            message: `Are you sure you want to delete the plan "${planName}"?\n\nThis action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setShowConfirmModal(false);
                await performDeletePlan(planId);
            }
        });
        setShowConfirmModal(true);
    };

    const performDeletePlan = async (planId) => {
        try {
            if (isGuestMode) {
                // Guest mode - remove from localStorage
                const updatedPlans = spendingPlans.filter(plan => plan.PlanId !== planId);
                setSpendingPlans(updatedPlans);
                saveToLocalStorage(GUEST_PLANS_KEY, updatedPlans);
                
                // Remove plan sections from localStorage
                const updatedSections = { ...apiSections };
                delete updatedSections[planId];
                setApiSections(updatedSections);
                saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
                
                toast.success('Guest spending plan deleted successfully!');
                return;
            }

            // Authenticated mode - use API
            const payload = {
                PlanId: planId
            };

    
            
            console.log('Deleting plan with payload:', payload);
            
            // Call delete API endpoint
            const result = await deleteSpendingPlan(payload);
            console.log('Plan deleted successfully:', result);
            
            // Refresh spending plans after successful deletion
            const plansData = await getSpendingPlans(patientId);
            setSpendingPlans(plansData?.data || []);
            
            // Also refresh plan lines data
            await refreshPlanData();
            
            toast.success('Spending plan deleted successfully!');
            
        } catch (error) {
            console.error('Error deleting plan:', error);
            toast.error('Failed to delete spending plan. Please try again.');
        }
    };

    // Authentication functions
    const handleAuth = async (e) => {
        e.preventDefault();
        if (isAuthenticating) return;
        
        setIsAuthenticating(true);
        
        try {
            if (authMode === 'register') {
                // Validate passwords match
                if (authForm.password !== authForm.confirmPassword) {
                    toast.error('Passwords do not match');
                    return;
                }
                
                // Validate date of birth (must be at least 18 years old)
                if (authForm.dateOfBirth) {
                    const birthDate = new Date(authForm.dateOfBirth);
                    const today = new Date();
                    const age = today.getFullYear() - birthDate.getFullYear();
                    // const monthDiff = today.getMonth() - birthDate.getMonth();
                    
                    // if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    //     age--;
                    // }
                    
                    if (age < 18) {
                        toast.error('You must be at least 18 years old to register');
                        return;
                    }
                }
                
                // Prospect registration: call getBusinessList and addPatient
                let createdPatientId = null;
                try {
                    console.log('Registering prospect user (local) for addPatient:', {
                        email: authForm.email,
                        firstName: authForm.firstName,
                        lastName: authForm.lastName,
                        dateOfBirth: authForm.dateOfBirth,
                        ankotaStatus: 'prospect'
                    });

                    // Fetch the business list data to include in payload
                    let businessListData = [];
                    try {
                        businessListData = await getBusinessList(businessId);
                        console.log('getBusinessList returned:', businessListData);
                    } catch (err) {
                        console.warn('getBusinessList failed, proceeding with empty list:', err);
                        businessListData = [];
                    }

                    // Build payload per sample. businessId must be 1 in all cases.
                    // Ensure patientStatus is passed as 4 (Prospect) when not provided
                    const patientStatusValue = (authForm.patientStatus !== undefined && authForm.patientStatus !== null && authForm.patientStatus !== '')
                        ? Number(authForm.patientStatus)
                        : 4;

                    // Ensure birthDate is passed in YYYY-MM-DD format as expected by the backend
                    const birthDateValue = authForm.dateOfBirth
                        ? new Date(authForm.dateOfBirth).toISOString().slice(0, 10)
                        : '';

                    const patientPayload = {
                        businessList: typeof businessListData === 'string' ? businessListData : JSON.stringify(businessListData),
                        businessId: businessId,
                        firstName: authForm.firstName || '',
                        lastName: authForm.lastName || '',
                        homePhone: authForm.homePhone || '555-123-5555',
                        address1: authForm.address1 || '',
                        ssn: authForm.ssn || '',
                        birthDate: birthDateValue,
                        patientStatus: patientStatusValue
                    };

                    console.log('Calling addPatient with payload:', patientPayload);
                    const addPatientResult = await addPatient(patientPayload);
                    console.log('addPatient result:', addPatientResult);

                    // Try to extract the newly created PatientID from the response
                    if (addPatientResult) {
                        createdPatientId = addPatientResult?.PatientID ?? addPatientResult?.PatientId ?? addPatientResult?.PatientID ?? addPatientResult?.data?.PatientID ?? null;
                        if (!createdPatientId) {
                            // Fallback: try to parse any numeric ID in the returned message
                            try {
                                const text = JSON.stringify(addPatientResult);
                                const m = text.match(/\b(\d{4,})\b/);
                                if (m) createdPatientId = Number(m[1]);
                            } catch (e) { /* ignore */ }
                        }
                    }

                    // If we have an ID, call the save-user and save-patient-contact endpoints
                    if (createdPatientId) {
                        try {
                            const userPayload = {
                                UserID: 0,
                                UserName: authForm.email || authForm.contactEmail || '',
                                Password: authForm.password || '',
                                MemberId: Number(createdPatientId),
                                // Include BusinessID for proper association when backend supports it
                                BusinessID: businessId ? Number(businessId) : undefined,
                                businessId: businessId ? Number(businessId) : undefined
                            };
                            console.log('Calling saveUser with payload:', userPayload);
                                    const saveUserResult = await saveUser(userPayload);
                                    console.log('saveUser result:', saveUserResult);
                                    // Try to extract created UserID and save it for later use
                                    const createdUserId = saveUserResult?.UserID ?? saveUserResult?.UserId ?? saveUserResult?.data?.UserID ?? saveUserResult?.data?.UserId ?? null;
                                    if (createdUserId) {
                                        console.log('Captured createdUserId from saveUser:', createdUserId);
                                        // attach to authForm/store later when persisting demo credentials
                                        // We'll merge into userCredentials below
                                        // keep local reference for debugging
                                        window.__createdUserId = createdUserId;
                                    }
                        } catch (err) {
                            console.error('saveUser failed:', err);
                        }

                        try {
                            const nowIso = new Date().toISOString();

                            // Build contact payload but only include fields that were actually provided
                            // Avoid sending placeholder/default values (no "Primary emergency contact", no default BusinessID=90, no AddBy/ModBy 'Admin')
                            const contactPayload = {
                                PatientID: Number(createdPatientId),
                                OperationType: 0,
                                AddDate: nowIso,
                                ModDate: nowIso
                            };

                            if (authForm.contactFirstName) contactPayload.FirstName = authForm.contactFirstName;
                            if (authForm.contactLastName) contactPayload.LastName = authForm.contactLastName;
                            if (authForm.contactEmail) contactPayload.Email = authForm.contactEmail;
                            if (authForm.contactMobilePhone) contactPayload.MobilePhone = authForm.contactMobilePhone;
                            if (authForm.contactAddress1) contactPayload.Address1 = authForm.contactAddress1;
                            if (authForm.contactCity) contactPayload.City = authForm.contactCity;
                            if (authForm.contactState) contactPayload.State = authForm.contactState;
                            if (authForm.contactPostalCode) contactPayload.PostalCode = authForm.contactPostalCode;
                            if (authForm.contactRelationshipId) contactPayload.PatientContactRelationshipID = Number(authForm.contactRelationshipId);
                            if (authForm.contactNote) contactPayload.Note = authForm.contactNote;
                            // Only include BusinessID when a valid businessId is present
                            if (businessId) contactPayload.BusinessID = Number(businessId);

                            console.log('Calling savePatientContact with payload (no dummy/defaults):', contactPayload);
                            const saveContactResult = await savePatientContact(contactPayload);
                            console.log('savePatientContact result:', saveContactResult);
                        } catch (err) {
                            console.error('savePatientContact failed:', err);
                        }

                        toast.success(`Registration successful! Patient created.`);
                    } else {
                        console.warn('Could not determine created PatientID from addPatient result:', addPatientResult);
                        toast.error('Registration not successful.');
                    }

                } catch (err) {
                    console.error('Error during addPatient flow:', err);
                    toast.error('Registration failed while creating patient. Please try again.');
                    return;
                }

                // For demo/backwards compatibility: Store credentials in localStorage to simulate account creation
                // Persist demo credentials including created patientId and optionally created user id
                const storedUserId = window.__createdUserId || null;
                const userCredentials = {
                    email: authForm.email,
                    password: authForm.password,
                    firstName: authForm.firstName,
                    lastName: authForm.lastName,
                    dateOfBirth: authForm.dateOfBirth,
                    // Only persist a patientId if the backend returned one. Avoid fabricating IDs locally.
                    patientId: (typeof createdPatientId !== 'undefined' && createdPatientId) ? createdPatientId : null,
                    userId: storedUserId // may be null if saveUser did not return one
                };
                localStorage.setItem('demoUserCredentials', JSON.stringify(userCredentials));

                // If we created a patient on the backend and there are guest plans in memory,
                // migrate those guest plans and their lines to the server under the newly created patient.
                if (createdPatientId) {
                    (async () => {
                        try {
                            const stored = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                            const createdBy = stored?.userId || window.__createdUserId || null;

                            // Find guest plans that were created in-memory (isGuestPlan flag)
                            const guestPlans = (spendingPlans || []).filter(p => p.isGuestPlan);
                            if (guestPlans.length > 0) {
                                toast.success('Migrating your saved guest plans to your new account...');
                            }

                            // helper to map section key back to plan_type
                            const planTypeFromSection = (sectionKey) => {
                                const map = {
                                    'livingarrangements': 'Living Arrangements',
                                    'employmentandcommunityparticipation': 'Employment and Community Participation',
                                    'healthandsafety': 'Health and Safety'
                                };
                                return map[sectionKey] || 'Other';
                            };

                            for (const guestPlan of guestPlans) {
                                try {
                                    const planPayload = {
                                        PatientId: createdPatientId,
                                        Name: guestPlan.Name || guestPlan.planName || '',
                                        Year: new Date(guestPlan.start_date).getFullYear() || new Date().getFullYear(),
                                        IsActive: guestPlan.IsActive ?? 1,
                                        StartDate: formatDateForApi(guestPlan.start_date || guestPlan.StartDate) || new Date().toISOString().split('T')[0],
                                        EndDate: formatDateForApi(guestPlan.end_date || guestPlan.EndDate) || new Date().toISOString().split('T')[0],
                                        BusinessId: businessId,
                                        businessId: businessId
                                    };
                                    if (createdBy) planPayload.CreatedByUserId = createdBy;

                                    const addedPlanResult = await addSpendingPlan(planPayload);
                                    // Try to extract the new plan id from common fields
                                    const newPlanId = addedPlanResult?.PlanId || addedPlanResult?.PlanID || addedPlanResult?.data?.PlanId || addedPlanResult?.data?.PlanID || null;
                                    if (!newPlanId) {
                                        console.warn('Could not determine new PlanId from addSpendingPlan result:', addedPlanResult);
                                        continue; // skip migrating lines for this plan
                                    }

                                    // Migrate lines for this guest plan from apiSections state
                                    const planSections = apiSections?.[guestPlan.PlanId] || {};
                                    for (const [sectionKey, rows] of Object.entries(planSections)) {
                                        for (const row of (rows || [])) {
                                            try {
                                                const linePayload = {
                                                    PlanId: newPlanId,
                                                    ServiceCode: row.serviceCode || '',
                                                    Provider: row.provider || '',
                                                    LiveIn: row.liveIn || false,
                                                    ServiceDescription: row.description || row.serviceCode || '',
                                                    IPPGoal: row.ippGoal || '',
                                                    BasePay: Number(row.basePay) || 0,
                                                    PricingUnit: Number(row.pricingUnit) || 0,
                                                    AnnualFrequency: Number(row.frequency) || 0,
                                                    TotalAnnualCost: Number(row.annualCost) || (Number(row.pricingUnit) * Number(row.frequency)) || 0,
                                                    AllocatedBudget: row.allocatedBudget !== null && row.allocatedBudget !== undefined ? Number(row.allocatedBudget) : null,
                                                    plan_type: planTypeFromSection(sectionKey),
                                                    PaymentModel: String(row.burdenRate ?? 0),
                                                    CustomServiceId: row.customServiceId || null,
                                                    BusinessId: businessId,
                                                    //businessId: businessId
                                                };
                                                if (createdBy) linePayload.CreatedByUserId = createdBy;
                                                await addSpendingPlanLine(linePayload);
                                            } catch (err) {
                                                console.error('Failed to migrate plan line:', err, row);
                                            }
                                        }
                                    }
                                } catch (err) {
                                    console.error('Failed to migrate guest plan:', err, guestPlan);
                                }
                            }

                            // Refresh plans for the new patient so the UI shows the migrated plans
                            try {
                                const plansData = await getSpendingPlans(createdPatientId);
                                const updatedPlans = plansData?.data || [];
                                setSpendingPlans(updatedPlans);
                                // refresh lines
                                await refreshPlanData();
                                toast.success('Guest plans migrated successfully.');
                            } catch (err) {
                                console.error('Failed to refresh plans after migration:', err);
                            }
                        } catch (err) {
                            console.error('Error during guest plan migration:', err);
                        }
                    })();
                }

                setAuthMode('login');
                // Reset the form after successful registration, keep email for convenience
                setAuthForm({
                    email: authForm.email, // Keep email for convenience
                    password: '',
                    firstName: '',
                    lastName: '',
                    confirmPassword: '',
                    dateOfBirth: ''
                });
                // DOB cleared via authForm reset above
            } else {
                // Login mode for prospects
                console.log('Logging in prospect user:', authForm.email);

                // Try API login first
                try {
                    const loginResult = await login({ username: authForm.email, password: authForm.password });
                    console.log('login result:', loginResult);

                    // Determine success/token
                    const token = loginResult?.token || loginResult?.accessToken || loginResult?.Token || loginResult?.tokenString || null;
                    // Some login endpoints return UserID/MemberID instead of a token — treat those as success as well
                    const hasId = Boolean(loginResult?.UserID || loginResult?.UserId || loginResult?.MemberID || loginResult?.MemberId || loginResult?.PatientID || loginResult?.PatientId);
                    const isSuccess = Boolean(token) || Boolean(loginResult?.success) || Boolean(loginResult?.isAuthenticated) || Boolean(loginResult?.User) || hasId;

                    if (isSuccess) {
                        if (token) localStorage.setItem('authToken', token);
                        toast.success(`Login successful! Welcome back.`);
                        setShowAuthModal(false);

                        setAuthForm({
                            email: '',
                            password: '',
                            firstName: '',
                            lastName: '',
                            confirmPassword: '',
                            dateOfBirth: ''
                        });

                        // If the API returned a patient/member id or user id, redirect so App picks it up and loads user data
                        try {
                            const returnedPatientId = loginResult?.MemberID || loginResult?.MemberId || loginResult?.PatientID || loginResult?.PatientId || null;
                            const returnedUserId = loginResult?.UserID || loginResult?.UserId || null;
                            if (returnedPatientId) {
                                try {
                                    const stored = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                                    const merged = {
                                        ...stored,
                                        patientId: returnedPatientId,
                                        userId: returnedUserId || stored.userId || null
                                    };
                                    localStorage.setItem('demoUserCredentials', JSON.stringify(merged));
                                    // Reload so App.js picks up the stored credentials
                                    window.location.reload();
                                } catch (e) {
                                    console.error('Failed to persist credentials after login:', e);
                                }
                            }
                        } catch (e) {
                            // ignore redirect errors
                        }
                        // DOB cleared via authForm reset above
                    } else {
                        // If API returned but did not indicate success, fallback to demo credentials
                        const storedCredentials = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                            if (storedCredentials.email === authForm.email && storedCredentials.password === authForm.password) {
                            toast.success(`Login successful! Welcome back, ${storedCredentials.firstName}! Your plan has been saved permanently and you can now edit it.`);
                            setShowAuthModal(false);
                            setAuthForm({ email: '', password: '', firstName: '', lastName: '', confirmPassword: '', dateOfBirth: '' });
                            // If we have a stored demo patientId, redirect so App picks it up and switches out of guest mode
                            if (storedCredentials.patientId) {
                                try {
                                    const merged = {
                                        ...storedCredentials,
                                        patientId: storedCredentials.patientId,
                                        userId: storedCredentials.userId || null
                                    };
                                    localStorage.setItem('demoUserCredentials', JSON.stringify(merged));
                                    window.location.reload();
                                } catch (e) {
                                    console.error('Failed to persist demo credentials during login fallback:', e);
                                }
                            }
                            // DOB cleared via authForm reset above
                        } else {
                            toast.error('Invalid email or password. Please check your credentials or register first.');
                            return;
                        }
                    }
                } catch (err) {
                    console.error('Login API failed, falling back to demo credentials:', err);
                    // Fallback to stored demo credentials
                    const storedCredentials = JSON.parse(localStorage.getItem('demoUserCredentials') || '{}');
                    if (storedCredentials.email === authForm.email && storedCredentials.password === authForm.password) {
                        toast.success(`Login successful! Welcome back, ${storedCredentials.firstName}! Your plan has been saved permanently and you can now edit it.`);
                        setShowAuthModal(false);
                        setAuthForm({ email: '', password: '', firstName: '', lastName: '', confirmPassword: '', dateOfBirth: '' });
                        if (storedCredentials.patientId) {
                            try {
                                const merged = {
                                    ...storedCredentials,
                                    patientId: storedCredentials.patientId,
                                    userId: storedCredentials.userId || null
                                };
                                localStorage.setItem('demoUserCredentials', JSON.stringify(merged));
                                window.location.reload();
                            } catch (e) {
                                console.error('Failed to persist demo credentials during login fallback (catch):', e);
                            }
                        }
                        // DOB cleared via authForm reset above
                    } else {
                        toast.error('Invalid email or password. Please check your credentials or register first.');
                        return;
                    }
                }
            }
        } catch (error) {
            console.error('Authentication error:', error);
            toast.error('Authentication failed. Please try again.');
        } finally {
            setIsAuthenticating(false);
        }
    };
    
    // const handleAuthFormChange = (field, value) => {
    //     setAuthForm(prev => ({
    //         ...prev,
    //         [field]: value
    //     }));
    // };

    // Authentication helpers
    const isUserLoggedIn = () => {
        try {
            return Boolean(localStorage.getItem('authToken') || localStorage.getItem('demoUserCredentials'));
        } catch (e) {
            return false;
        }
    };

    // Logout helper - clears demo credentials and auth token and returns to base app
    const handleLogout = () => {
        try {
            localStorage.removeItem('authToken');
            localStorage.removeItem('demoUserCredentials');
        } catch (e) { /* ignore */ }
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('patientId');
            url.searchParams.delete('userId');
            window.location.href = url.toString();
        } catch (e) {
            // fallback to reload
            window.location.reload();
        }
    };

    // Toggle plan accordion
    // Ensure plan lines are loaded when a plan is expanded
    const ensurePlanLinesLoaded = async (planId) => {
        try {
            if (apiSections[planId] && Object.keys(apiSections[planId]).length > 0) {
                return;
            }
            setPlanLinesLoading(prev => ({ ...prev, [planId]: true }));
            const linesData = await getSpendingPlanLines(planId);
            const lines = linesData?.data || [];

            const groupedByPlanType = lines.reduce((acc, line) => {
                const planType = line.plan_type || 'Other';
                const sectionKey = planType.toLowerCase().replace(/\s+/g, '').replace(/[^a-z]/g, '');
                if (!acc[sectionKey]) acc[sectionKey] = [];

                const basePay = parseFloat(line.BasePay) || 0;
                const burdenRate = parseFloat(line.PaymentModel) || 0;
                const correctPricingUnit = basePay + (basePay * burdenRate / 100);
                const allocatedBudgetValue = (line.AllocatedBudget !== null && line.AllocatedBudget !== undefined) ? parseFloat(line.AllocatedBudget) : null;

                acc[sectionKey].push({
                    id: line.LineId,
                    serviceCode: line.ServiceCode || '',
                    provider: line.Provider || line.ProviderName || '',
                    liveIn: line.LiveIn || false,
                    description: line.ServiceDescription || line.Description || '',
                    ippGoal: line.IPPGoal || '',
                    basePay: basePay,
                    burdenRate: burdenRate,
                    pricingUnit: correctPricingUnit,
                    frequency: parseInt(line.AnnualFrequency) || 0,
                    annualCost: parseFloat(line.TotalAnnualCost) || 0,
                    allocatedBudget: allocatedBudgetValue,
                    customServiceId: line.CustomServiceId || line.ServiceId || line.ServiceID || null,
                    isApi: true,
                    planId: planId,
                    originalLine: line
                });
                return acc;
            }, {});

            setApiSections(prev => ({ ...prev, [planId]: groupedByPlanType }));
        } catch (error) {
            console.error('Error loading plan lines for planId', planId, error);
        } finally {
            setPlanLinesLoading(prev => ({ ...prev, [planId]: false }));
        }
    };

    const togglePlanExpansion = async (index, planId) => {
        setExpandedPlans(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(index)) {
                newExpanded.delete(index);
            } else {
                newExpanded.add(index);
            }
            return newExpanded;
        });
        // If expanding, trigger lazy load in background
        if (!expandedPlans.has(index)) {
            ensurePlanLinesLoaded(planId);
        }
    };

    const handlePlanFormChange = (field, value) => {
        setNewPlanForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Filter rows based on search query
    const filterRowsBySearch = (rows) => {
        if (!searchQuery || searchQuery.trim() === '') {
            return rows;
        }

        const query = searchQuery.toLowerCase().trim();
        
        return rows.filter(row => {
            // Get the service description from service code options
            const serviceCodeOption = serviceCodeOptions.find(opt => 
                (opt.ServiceTypeID === row.serviceCode || opt.Name === row.serviceCode)
            );
            const serviceDescription = serviceCodeOption?.Description || serviceCodeOption?.ServiceDescription || serviceCodeOption?.Name || row.serviceCode || '';
            
            // Search across multiple fields
            const searchableFields = [
                String(row.serviceCode || ''),
                String(row.provider || ''),
                String(row.description || ''),
                String(serviceDescription),
                String(row.ippGoal || '')
            ];
            
            return searchableFields.some(field => 
                field.toLowerCase().includes(query)
            );
        });
    };

    // Render plan lines data with interactive functionality (same as original tables)
    const renderPlanLinesData = (tabType) => {
        let filteredPlans = [];
        
        switch(tabType) {
            case 'previous':
                filteredPlans = getPreviousPlans();
                break;
            case 'current':
                filteredPlans = getCurrentPlans();
                break;
            case 'future':
                filteredPlans = getFuturePlans();
                break;
            default:
                return null;
        }

        if (filteredPlans.length === 0) {
            // Show empty state when no plans exist (guest mode or authenticated user with no plans)
            if (spendingPlans.length === 0) {
                return (
                    <div className="text-center py-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Create Your First Spending Plan</h3>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                            {isGuestMode 
                                ? "Start by creating a spending plan to organize your services and budget. You can fill it out, print it, and save it for future editing."
                                : "You don't have any spending plans yet. Create your first plan to start organizing your services and budget."
                            }
                        </p>
                        <button
                            onClick={() => setShowAddPlanModal(true)}
                            className="bg-primary-dark text-white px-6 py-3 rounded-lg shadow hover:shadow-lg transition-all duration-200 font-medium"
                        >
                            Create Your First Plan
                        </button>
                    </div>
                );
            }
            return null;
        }

        // Map plan_type to section titles with service code ranges
        const sectionTitles = {
            'livingarrangements': 'Living Arrangements (310–330)',
            'employmentandcommunityparticipation': 'Employment and Community Participation (331–355)', 
            'healthandsafety': 'Health and Safety (356–399)'
        };

        return (
            <div>
                {/* Header for tab */}
                <div className="mb-6">
                    <h3 className="text-lg font-bold mb-4">{tabType.charAt(0).toUpperCase() + tabType.slice(1)} Plans ({filteredPlans.length})</h3>
                </div>

                {/* Render each plan with its data immediately following */}
                {filteredPlans.map((plan, index) => {
                    const planApiSections = apiSections[plan.PlanId] || {};
                    const isExpanded = expandedPlans.has(index);
                    const hasPendingChanges = Object.values(planApiSections).flat().some(row => 
                        modifiedRows.has(`${plan.PlanId}-livingarrangements-${row.id}`) ||
                        modifiedRows.has(`${plan.PlanId}-employmentandcommunityparticipation-${row.id}`) ||
                        modifiedRows.has(`${plan.PlanId}-healthandsafety-${row.id}`)
                    );
                    
                    return (
                        <div key={plan.PlanId} className="mb-8">
                            {/* Plan Information - Clickable Accordion Header */}
                            <div 
                                className={`bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 transition-all duration-200 ${isExpanded ? 'shadow-md' : ''}`}
                            >
                                <div className="flex items-start">
                                    <div className="flex-1">
                                        {/* Plan Name - Editable */}
                                        {!isGuestMode ? (
                                            <div className="text-lg font-semibold text-gray-800 text-left mb-2">
                                                {editingPlanName === plan.PlanId ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            className="border rounded px-2 py-1 text-lg font-semibold"
                                                            value={planNameForms[plan.PlanId]?.planName || plan.Name || ''}
                                                            placeholder="Enter plan name"
                                                            onChange={(e) => setPlanNameForms(prev => ({
                                                                ...prev,
                                                                [plan.PlanId]: {
                                                                    planName: e.target.value
                                                                }
                                                            }))}
                                                        />
                                                        <button
                                                            onClick={() => handleSavePlanName(plan.PlanId)}
                                                            disabled={savingPlanName === plan.PlanId}
                                                            className="bg-primary-dark text-white px-2 py-1 rounded text-xs hover:bg-primary-darker disabled:opacity-50 flex items-center gap-1"
                                                        >
                                                            {savingPlanName === plan.PlanId ? (
                                                                <>
                                                                    <svg className="w-3 h-3 animate-spin" fill="currentColor" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                    Saving...
                                                                </>
                                                            ) : (
                                                                'Save'
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setEditingPlanName(null);
                                                                setPlanNameForms(prev => {
                                                                    const newForms = { ...prev };
                                                                    delete newForms[plan.PlanId];
                                                                    return newForms;
                                                                });
                                                            }}
                                                            className="bg-gray-300 text-primary-dark px-2 py-1 rounded text-xs hover:text-white hover:bg-gray-600"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <span>{plan.Name || 'Unnamed Plan'}</span>
                                                        <button
                                                            onClick={() => {
                                                                setEditingPlanName(plan.PlanId);
                                                                setPlanNameForms(prev => ({
                                                                    ...prev,
                                                                    [plan.PlanId]: {
                                                                        planName: plan.Name || ''
                                                                    }
                                                                }));
                                                            }}
                                                            className="text-gray-400 hover:text-blue-600 p-1"
                                                            title="Edit plan name"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-lg font-semibold text-gray-800 text-left mb-2">
                                                {plan.Name || 'Unnamed Plan'}
                                            </div>
                                        )}
                                        {!isGuestMode ? (
                                            <div className="text-sm text-gray-600 text-left">
                                                <span className="mr-2">Period:</span>
                                                {editingPlanDates === plan.PlanId ? (
                                                    <div className="inline-flex items-center gap-2">
                                                        <DatePicker
                                                            selected={parseDate(planDateForms[plan.PlanId]?.startDate || plan.start_date)}
                                                            onChange={(date) => setPlanDateForms(prev => ({
                                                                ...prev,
                                                                [plan.PlanId]: {
                                                                    ...prev[plan.PlanId],
                                                                    startDate: date
                                                                }
                                                            }))}
                                                            dateFormat="MM/dd/yyyy"
                                                            placeholderText="MM/DD/YYYY"
                                                            className="border rounded px-2 py-1 text-sm"
                                                        />
                                                        <span>-</span>
                                                        <DatePicker
                                                            selected={parseDate(planDateForms[plan.PlanId]?.endDate || plan.end_date)}
                                                            onChange={(date) => setPlanDateForms(prev => ({
                                                                ...prev,
                                                                [plan.PlanId]: {
                                                                    ...prev[plan.PlanId],
                                                                    endDate: date
                                                                }
                                                            }))}
                                                            dateFormat="MM/dd/yyyy"
                                                            placeholderText="MM/DD/YYYY"
                                                            className="border rounded px-2 py-1 text-sm"
                                                        />
                                                        <button
                                                            onClick={() => handleSavePlanDates(plan.PlanId)}
                                                            disabled={savingPlanDates === plan.PlanId}
                                                            className="bg-primary-dark text-white px-2 py-1 rounded text-xs hover:bg-primary-darker disabled:opacity-50 flex items-center gap-1"
                                                        >
                                                            {savingPlanDates === plan.PlanId ? (
                                                                <>
                                                                    <svg className="w-3 h-3 animate-spin" fill="currentColor" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                    Saving...
                                                                </>
                                                            ) : (
                                                                'Save'
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setEditingPlanDates(null);
                                                                setPlanDateForms(prev => {
                                                                    const newForms = { ...prev };
                                                                    delete newForms[plan.PlanId];
                                                                    return newForms;
                                                                });
                                                            }}
                                                            className="bg-gray-300 text-primary-dark px-2 py-1 rounded text-xs hover:text-white hover:bg-gray-600"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <span>{formatDateUS(plan.start_date)} - {formatDateUS(plan.end_date)}</span>
                                                        <button
                                                            onClick={() => {
                                                                setEditingPlanDates(plan.PlanId);
                                                                console.log('Raw dates from plan:', { start_date: plan.start_date, end_date: plan.end_date });
                                                                setPlanDateForms(prev => ({
                                                                    ...prev,
                                                                    [plan.PlanId]: {
                                                                        startDate: parseDate(plan.start_date),
                                                                        endDate: parseDate(plan.end_date)
                                                                    }
                                                                }));
                                                            }}
                                                            className="text-gray-400 hover:text-blue-600 p-1"
                                                            title="Edit plan dates"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-600 text-left">
                                                Period: {formatDateUS(plan.start_date)} - {formatDateUS(plan.end_date)}
                                            </p>
                                        )}
                                        <p className="text-sm text-gray-600 text-left">
                                            Status: {plan.IsActive ? 'Active' : 'Inactive'} | Year: {plan.Year}
                                            {hasPendingChanges && (
                                                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    Unsaved Changes
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-lg font-semibold text-blue-600 text-left">
                                            Total Budget: ${
                                                Object.values(apiSections[plan.PlanId] || {})
                                                    .flat()
                                                    .reduce((sum, row) => sum + (Number(row.pricingUnit || 0) * Number(row.frequency || 0)), 0)
                                                    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            }
                                        </p>
                                        {!isExpanded && (
                                            <div className="mt-3 p-2 bg-white rounded border">
                                                <p className="text-sm text-gray-500 text-left">
                                                    <span className="font-medium">
                                                        {Object.values(apiSections[plan.PlanId] || {}).flat().length} items
                                                    </span>
                                                    {" | "}
                                                    <span>
                                                        {Object.keys(apiSections[plan.PlanId] || {}).length} sections
                                                    </span>
                                                    {" | "}
                                                    <span className="text-blue-600">Click to expand</span>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {/* Export Plan Button */}
                                        <div className="relative">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDropdownOpen(dropdownOpen === `export-${plan.PlanId}` ? false : `export-${plan.PlanId}`);
                                                }}
                                                className="bg-primary-dark text-white px-4 py-2 rounded-lg shadow hover:bg-primary-hover transition-all duration-200 flex items-center gap-2"
                                            >
                                                <img src="/print.svg" alt="Print" className="w-5 h-5" />
                                                Export
                                            </button>
                                            {dropdownOpen === `export-${plan.PlanId}` && (
                                                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-50">
                                                    <button 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleExportToExcel(plan, planApiSections);
                                                            setDropdownOpen(false);
                                                        }} 
                                                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                                                    >
                                                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        Export as Excel
                                                    </button>
                                                    <button 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleExportToPDF(plan, planApiSections);
                                                            setDropdownOpen(false);
                                                        }} 
                                                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                                                    >
                                                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        Export as PDF
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Save My Plan Button - only show for guest users */}
                                        {isGuestMode && (
                                            <button
                                                onClick={() => {
                                                    setAuthMode('register');
                                                    setShowAuthModal(true);
                                                }}
                                                className="bg-primary-dark text-white px-4 py-2 rounded-lg shadow hover:shadow-lg transition-all duration-200 font-medium flex items-center gap-2"
                                                title="Save My Plan"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                                </svg>
                                                Save My Plan
                                            </button>
                                        )}

                                        {/* Delete Plan Button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const planDisplayName = plan.Name || plan.planName || `Plan #${plan.PlanId}`;
                                                handleDeletePlan(plan.PlanId, `${planDisplayName} (${formatDateUS(plan.start_date)})`);
                                            }}
                                            className="bg-danger-dark text-white px-4 py-2 rounded-lg shadow hover:from-red-700 hover:to-red-800 transition-all duration-200 flex items-center gap-2"
                                            title="Delete Plan"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Delete Plan
                                        </button>
                                        
                                        {/* Accordion Toggle Icon */}
                                        <svg 
                                            className={`w-6 h-6 text-blue-600 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''} cursor-pointer`} 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                togglePlanExpansion(index, plan.PlanId);
                                            }}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Plan Content - Conditionally Rendered */}
                            {isExpanded && (
                                <div className="plan-content">
                                    {/* Show loader while lines are being fetched for this plan */}
                                    {(!apiSections[plan.PlanId] || Object.keys(apiSections[plan.PlanId]).length === 0) && (
                                        planLinesLoading[plan.PlanId] ? (
                                            <div className="text-center py-10">
                                                <div className="inline-flex items-center justify-center w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                                <p className="mt-3 text-gray-500">Loading services for this plan…</p>
                                            </div>
                                        ) : null
                                    )}
                                    {/* Plan Data Tables - Always show all 3 sections */}
                            {Object.entries(sectionTitles).map(([sectionKey, sectionTitle]) => {
                                const allRows = planApiSections[sectionKey] || [];
                                const rows = filterRowsBySearch(allRows);
                        
                                return (
                                    <section key={`${plan.PlanId}-${sectionKey}`} className="mb-10 2xl:my-20 rounded-2xl shadow-lg p-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <h2 className="text-lg 2xl:text-3xl font-bold">{sectionTitle}</h2>
                                            <Button 
                                                className="2xl:text-lg 2xl:px-4 2xl:py-2 bg-primary-dark hover:bg-primary-dark text-white rounded-lg shadow" 
                                                onClick={() => handleApiAddRow(plan.PlanId, sectionKey)}
                                                title="Add New Row"
                                            >
                                                Add New Row
                                            </Button>
                                        </div>
                                        
                                        {rows.length === 0 ? (
                                            /* Empty state for sections with no data or no search results */
                                            <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                                                <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
                                                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        {searchQuery ? (
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        ) : (
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                        )}
                                                    </svg>
                                                </div>
                                                <h3 className="text-sm font-medium text-gray-900 mb-1">
                                                    {searchQuery ? `No results found for "${searchQuery}"` : 'No services in this section'}
                                                </h3>
                                                <p className="text-xs text-gray-500 mb-3">
                                                    {searchQuery ? 'Try adjusting your search terms' : 'Get started by adding your first service line item'}
                                                </p>
                                                {!searchQuery && (
                                                    <Button 
                                                        className="text-sm bg-primary-dark hover:bg-primary-dark text-white rounded-lg shadow" 
                                                    onClick={() => handleApiAddRow(plan.PlanId, sectionKey)}
                                                    title="Add First Service"
                                                >
                                                    Add First Service
                                                </Button>
                                                )}
                                            </div>
                                        ) : (
                                            /* Existing table structure for sections with data */
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[1100px] border-separate border-spacing-0 rounded-xl overflow-hidden 2xl:text-lg 2xl:my-12 bg-white">
                                        <thead>
                                            <tr className="bg-[#7da7d9] text-left text-sm 2xl:text-xl 2xl:h-20 font-semibold text-white">
                                                <th className="p-3 pt-4 w-14"></th>
                                                <th className="p-3">Service Code</th>
                                                <th className="p-3">Provider</th>
                                                <th className="p-3 text-center">Not EVV</th>
                                                <th className="p-3">Service Description</th>
                                                <th className="p-3">IPP Goal</th>
                                                <th className="p-3">Base Pay</th>
                                                <th className="p-3 min-w-[160px]">Burden Rate</th>
                                                <th className="p-3">Pricing Unit</th>
                                                <th className="p-3 text-center">Annual Frequency</th>
                                                <th className="p-3 text-center">Total Annual Cost</th>
                                                <th className="p-3 text-center">Allocated Budget</th>
                                                <th className="p-3">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                // Group rows by service code
                                                const groupedRows = rows.reduce((acc, row) => {
                                                    const serviceCode = row.serviceCode || 'No Service Code';
                                                    if (!acc[serviceCode]) acc[serviceCode] = [];
                                                    acc[serviceCode].push(row);
                                                    return acc;
                                                }, {});

                                                return Object.entries(groupedRows).map(([serviceCode, serviceRows]) => (
                                                    <React.Fragment key={serviceCode}>
                                                        {serviceRows.map((row) => (
                                                            <tr key={row.id} className="bg-[#f6fbfa] hover:bg-[#f3f6fa] border-b border-[#e0e7ef] text-sm 2xl:text-xl 2xl:h-16 transition">
                                                                <td className="px-2 py-4 flex gap-1">
                                                                    <button 
                                                                            className="text-gray-400 hover:text-gray-600"
                                                                            onClick={() => handleApiCopyRow(plan.PlanId, sectionKey, row)}
                                                                            title="Copy Row"
                                                                        >
                                                                            <img src="/copy-icon.png" alt="Copy" className="w-4 h-4" />
                                                                        </button>
                                                                        <button 
                                                                            className="text-red-400 hover:text-red-600 ml-1"
                                                                            onClick={() => {
                                                                                console.log('Delete button clicked for row:', row);
                                                                                handleApiDeleteRow(plan.PlanId, sectionKey, row);
                                                                            }}
                                                                            title="Delete Row"
                                                                        >
                                                                            <img src="/group_1.svg" alt="Delete" className="w-4 h-4" />
                                                                        </button>
                                                                </td>
                                                    {/* Service Code - Dropdown */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "serviceCode" })}>
                                                        {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "serviceCode" ? (
                                                            <div>
                                                                <div className="text-xs text-gray-500 mb-1 font-semibold">
                                                                    {getServiceCodeLabel(sectionKey)}
                                                                </div>
                                                                <select
                                                                    autoFocus
                                                                    className="w-full border rounded px-1"
                                                                    value={row.serviceCode}
                                                                    onChange={(e) => {
                                                                        const selectedServiceCode = e.target.value;
                                                                        
                                                                        console.log('Service code dropdown onChange - selected:', selectedServiceCode);
                                                                        console.log('Current row.serviceCode before update:', row.serviceCode);
                                                                        
                                                                        // Find the selected service code object from the same filtered list used in dropdown
                                                                        const filteredOptions = getFilteredServiceCodes(sectionKey);
                                                                        const selectedOption = filteredOptions.find(opt => (opt.ServiceTypeID || opt.Name) === selectedServiceCode);
                                                                        
                                                                        console.log('Selected option found:', selectedOption);
                                                                        
                                                                        // Update multiple fields at once to avoid state conflicts
                                                                        const provider = selectedOption?.Provider || selectedOption?.DefaultProvider || '';
                                                                        const description = selectedOption?.Description || selectedOption?.ServiceDescription || selectedOption?.Name || '';
                                                                        
                                                                        // Update the sections state directly with all changes
                                                                        const updatedSections = {
                                                                            ...apiSections,
                                                                            [plan.PlanId]: {
                                                                                ...apiSections[plan.PlanId],
                                                                                [sectionKey]: apiSections[plan.PlanId][sectionKey].map(r => {
                                                                                    if (r.id === row.id) {
                                                                                        const updatedRow = { 
                                                                                            ...r, 
                                                                                            serviceCode: selectedServiceCode,
                                                                                            description: description
                                                                                        };
                                                                                        
                                                                                        // Also update provider if available
                                                                                        if (provider) {
                                                                                            updatedRow.provider = provider;
                                                                                        }
                                                                                        
                                                                                        console.log('Updated row:', updatedRow);
                                                                                        return updatedRow;
                                                                                    }
                                                                                    return r;
                                                                                })
                                                                            }
                                                                        };

                                                                        setApiSections(updatedSections);
                                                                        
                                                                        // Mark row as modified
                                                                        const rowKey = `${plan.PlanId}-${sectionKey}-${row.id}`;
                                                                        setModifiedRows(prev => new Set([...prev, rowKey]));
                                                                        
                                                                        // Save to localStorage in guest mode
                                                                        if (isGuestMode) {
                                                                            saveToLocalStorage(GUEST_SECTIONS_KEY, updatedSections);
                                                                        }
                                                                        
                                                                        // Force close the dropdown after selection
                                                                        setTimeout(() => {
                                                                            setEditingCell({ sectionKey: null, rowId: null, field: null });
                                                                        }, 100);
                                                                    }}
                                                                    onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                >
                                                                    <option value="">-- Select Service Description --</option>
                                                                    {(() => {
                                                                        const options = getFilteredServiceCodes(sectionKey);
                                                                        console.log('Dropdown options for', sectionKey, ':', options);
                                                                        return options.map((opt) => (
                                                                            <option key={opt.ServiceTypeID || opt.Name} value={opt.ServiceTypeID || opt.Name}>
                                                                                {opt.Description || opt.ServiceDescription || opt.Name || opt.ServiceTypeID}
                                                                            </option>
                                                                        ));
                                                                    })()}
                                                                </select>
                                                            </div>
                                                        ) : (() => {
                                                            // Display service description instead of code
                                                            const serviceCodeOption = serviceCodeOptions.find(opt => 
                                                                (opt.ServiceTypeID === row.serviceCode || opt.Name === row.serviceCode)
                                                            );
                                                            const displayText = serviceCodeOption?.Description || serviceCodeOption?.ServiceDescription || serviceCodeOption?.Name || row.serviceCode;
                                                            return displayText || <span className="text-gray-400 italic">Select service description</span>;
                                                        })()}
                                                    </td>
                                                    {/* Provider - Editable */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "provider" })}>
                                                        {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "provider" ? (
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                className="w-full border rounded px-1"
                                                                value={row.provider}
                                                                placeholder="Enter provider"
                                                                onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "provider", e.target.value)}
                                                                onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingCell({ sectionKey: null, rowId: null, field: null }); }}
                                                            />
                                                        ) : (row.provider || <span className="text-gray-400 italic">Enter provider</span>)}
                                                    </td>
                                                    {/* Not EVV - Checkbox */}
                                                    <td className="p-2 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={row.liveIn || false}
                                                            onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "liveIn", e.target.checked)}
                                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                                        />
                                                    </td>
                                                    {/* Description - Non-editable, only override via add/edit */}
                                                    <td className="p-2">
                                                        <div className="flex items-center gap-1">
                                                            <div className="flex-1">
                                                                {row.description || <span className="text-gray-400 italic">Select service code</span>}
                                                            </div>
                                                            {/* Add/Duplicate/Edit custom service type name is intentionally not offered here for
                                                                now — that capability stays exclusive to the embedded (V2) version. */}
                                                        </div>
                                                    </td>
                                                    {/* IPP Goal - Editable */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "ippGoal" })}>
                                                        {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "ippGoal" ? (
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                className="w-full border rounded px-1"
                                                                value={row.ippGoal}
                                                                placeholder="Enter IPP goal"
                                                                onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "ippGoal", e.target.value)}
                                                                onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingCell({ sectionKey: null, rowId: null, field: null }); }}
                                                            />
                                                        ) : (row.ippGoal || <span className="text-gray-400 italic">Enter IPP goal</span>)}
                                                    </td>
                                                    {/* Base Pay - Editable */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "basePay" })}>
                                                        {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "basePay" ? (
                                                            <input
                                                                autoFocus
                                                                type="number"
                                                                step="1"
                                                                min="0"
                                                                className="w-full border rounded px-1"
                                                                value={row.basePay === 0 ? '' : row.basePay}
                                                                placeholder="Enter amount"
                                                                onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "basePay", e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                                                onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingCell({ sectionKey: null, rowId: null, field: null }); }}
                                                            />
                                                        ) : (row.basePay ? `$${Number(row.basePay).toFixed(2)}` : <span className="text-gray-400 italic">Enter base pay</span>)}
                                                    </td>
                                                    {/* Burden Rate - business-configured dropdown (Direct/Indirect etc.) when a businessId-only
                                                        query param is present; otherwise a free-entry percentage */}
                                                    <td className="p-2 min-w-[160px] whitespace-nowrap" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "burdenRate" })}>
                                                        {burdenRateOptions.length > 0 ? (
                                                            editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "burdenRate" ? (
                                                                <select
                                                                    autoFocus
                                                                    className="w-full min-w-[160px] border rounded px-1"
                                                                    value={burdenRateOptions.some(opt => opt.value === Number(row.burdenRate)) ? Number(row.burdenRate) : ''}
                                                                    onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "burdenRate", e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                                                    onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                >
                                                                    <option value="">-- Select --</option>
                                                                    {burdenRateOptions.map((opt, i) => (
                                                                        <option key={`${opt.label}-${i}`} value={opt.value}>{opt.label} ({opt.value}%)</option>
                                                                    ))}
                                                                </select>
                                                            ) : (row.burdenRate ? `${burdenRateOptions.find(opt => opt.value === Number(row.burdenRate))?.label || ''} (${Number(row.burdenRate).toFixed(2)}%)` : <span className="text-gray-400 italic">Select</span>)
                                                        ) : (
                                                            editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "burdenRate" ? (
                                                                <input
                                                                    autoFocus
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    className="w-full border rounded px-1"
                                                                    value={row.burdenRate === 0 ? '' : row.burdenRate}
                                                                    placeholder="Enter %"
                                                                    onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "burdenRate", e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                                                    onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingCell({ sectionKey: null, rowId: null, field: null }); }}
                                                                />
                                                            ) : (row.burdenRate ? `${Number(row.burdenRate).toFixed(2)}%` : <span className="text-gray-400 italic">Enter %</span>)
                                                        )}
                                                    </td>
                                                    {/* Pricing Unit - Calculated */}
                                                    <td className="p-2">${Number(row.pricingUnit).toFixed(2)}</td>
                                                    {/* Frequency - Editable + Calculator */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "frequency" })}>
                                                        <div className="flex items-center justify-between">
                                                             <div className="flex-1 flex justify-between mr-2">
                                                                <CalculatorModal
                                                                    row={row}
                                                                    sectionKey={`${plan.PlanId}-${sectionKey}`}
                                                                    field="frequency"
                                                                    onUpdateRow={(updatedRow) => {
                                                                        // Calculator will push updated frequency values back to the table
                                                                        handleApiChange(plan.PlanId, sectionKey, row.id, "frequency", updatedRow.frequency);
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="flex-1 text-center">
                                                                {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "frequency" ? (
                                                                        <input
                                                                            autoFocus
                                                                            type="number"
                                                                            step="1"
                                                                            min="1"
                                                                            className="w-20 border rounded px-1 text-left"
                                                                            value={row.frequency === '' ? '' : row.frequency}
                                                                            onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "frequency", e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                                            onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingCell({ sectionKey: null, rowId: null, field: null }); }}
                                                                        />
                                                                ) : (
                                                                    Number(row.frequency || 1).toFixed(0)
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {/* Annual Cost - Calculated (Read-only) with budget comparison */}
                                                    <td className="p-2 text-center">
                                                        <div className="flex flex-col items-center">
                                                            <div>${(Number(row.pricingUnit) * Number(row.frequency) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                            {row.allocatedBudget && (
                                                                <div className={`text-xs mt-1 px-2 py-1 rounded ${
                                                                    Number(row.pricingUnit) * Number(row.frequency) > Number(row.allocatedBudget) ? 'bg-red-100 text-red-600' :
                                                                    Number(row.pricingUnit) * Number(row.frequency) < Number(row.allocatedBudget) ? 'bg-green-100 text-green-600' :
                                                                    'bg-yellow-100 text-yellow-600'
                                                                }`}>
                                                                    {Number(row.pricingUnit) * Number(row.frequency) > Number(row.allocatedBudget) ? 'Over Budget' :
                                                                     Number(row.pricingUnit) * Number(row.frequency) < Number(row.allocatedBudget) ? 'Under Budget' :
                                                                     'On Budget'}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    {/* Allocated Budget - Editable */}
                                                    <td className="p-2" onClick={() => setEditingCell({ sectionKey: `${plan.PlanId}-${sectionKey}`, rowId: row.id, field: "allocatedBudget" })}>
                                                        {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}` && editingCell.rowId === row.id && editingCell.field === "allocatedBudget" ? (
                                                            <input
                                                                autoFocus
                                                                type="number"
                                                                step="0.01"
                                                                className="w-full border rounded px-1"
                                                                value={row.allocatedBudget || ''}
                                                                placeholder="Enter budget"
                                                                onChange={(e) => handleApiChange(plan.PlanId, sectionKey, row.id, "allocatedBudget", e.target.value)}
                                                                onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditingCell({ sectionKey: null, rowId: null, field: null }); }}
                                                            />
                                                        ) : (
                                                            <div className="text-center">
                                                                {(row.allocatedBudget !== null && row.allocatedBudget !== undefined && row.allocatedBudget !== '' && !isNaN(row.allocatedBudget)) ? 
                                                                    `$${Number(row.allocatedBudget).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 
                                                                    <span className="text-gray-400 italic">Enter budget</span>
                                                                }
                                                            </div>
                                                        )}
                                                    </td>
                                                    {/* Actions */}
                                                    <td className="p-2">
                                                        <div className="flex gap-1">
                                                            {/* Save Button - Always visible, changes style based on state */}
                                                                <button 
                                                                    className={`px-3 py-1 rounded text-xs font-semibold transition-colors min-w-[50px] ${
                                                                        (modifiedRows.has(`${plan.PlanId}-${sectionKey}-${row.id}`) || row.isNew) 
                                                                            ? "bg-green-500 text-white hover:bg-green-600 shadow-md" 
                                                                            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                                                                    }`}
                                                                    onClick={() => handleApiSave(plan.PlanId, sectionKey, row)}
                                                                    disabled={savingRows.has(`${plan.PlanId}-${sectionKey}-${row.id}`)}
                                                                    title={`${row.isNew ? 'Add New' : 'Save Changes'} Row`}
                                                                >
                                                                    {savingRows.has(`${plan.PlanId}-${sectionKey}-${row.id}`) ? (
                                                                        <svg className="w-3 h-3 animate-spin mx-auto" fill="currentColor" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                    ) : (
                                                                        row.isNew ? 'ADD' : 'SAVE'
                                                                    )}
                                                                </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            
                                            {/* Service Code Subtotal - Show only if more than 1 record for this service code */}
                                            {serviceRows.length > 1 && (
                                                <tr className="bg-[#e8f4f8] text-gray-700 border-b border-[#c2d9e0] font-semibold">
                                                    <td colSpan={9} className="p-2 text-center text-sm">
                                                        {serviceCode} Subtotal ({serviceRows.length} records)
                                                    </td>
                                                    <td className="p-2 ps-14 text-center text-sm">
                                                        {serviceRows.reduce((sum, r) => sum + Number(r.frequency || 0), 0).toFixed(2)}
                                                    </td>
                                                    <td className="p-2 text-center text-sm">
                                                        ${serviceRows.reduce((sum, r) => sum + (Number(r.pricingUnit || 0) * Number(r.frequency || 0)), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="p-2 text-center text-sm">
                                                        ${serviceRows.reduce((sum, r) => sum + (Number(r.allocatedBudget || 0)), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </td>
                                                    <td className="p-2"></td>
                                                </tr>
                                            )}
                                            
                                            {/* Approved Budget Row - Show only in patient mode (not guest mode) */}
                                            {!isGuestMode && (
                                                <tr className="bg-[#f0f8ff] text-gray-600 border-b border-[#c2d9e0]">
                                                    <td colSpan={13} className="p-3">
                                                        <div className="flex items-center justify-between w-full border border-[#c2d9e0] rounded-md p-3 shadow-sm">
                                                            <span className="text-sm font-medium">
                                                                Approved Budget for {serviceCode}:
                                                            </span>
                                                            <div className="flex items-center gap-6">
                                                                <div className="text-center">
                                                                    <div className="text-xs text-gray-500 mb-1">Actual Total</div>
                                                                    <span className="text-sm font-semibold">
                                                                        ${serviceRows.reduce((sum, r) => sum + (Number(r.pricingUnit || 0) * Number(r.frequency || 0)), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </span>
                                                                </div>
                                                                <div className="text-center" onClick={() => {
                                                                    const budgetKey = `${plan.PlanId}-${sectionKey}-${serviceCode}`;
                                                                    setEditingCell({ sectionKey: budgetKey, rowId: 'budget', field: 'approved' });
                                                                }}>
                                                                    <div className="text-xs text-gray-500 mb-1">Approved Budget</div>
                                                                    {editingCell.sectionKey === `${plan.PlanId}-${sectionKey}-${serviceCode}` && editingCell.rowId === 'budget' && editingCell.field === 'approved' ? (
                                                                        <input
                                                                            autoFocus
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            className="w-32 border rounded px-2 py-1 text-sm text-center"
                                                                            placeholder="$0.00"
                                                                            value={approvedBudgets[`${plan.PlanId}-${sectionKey}-${serviceCode}`] || ''}
                                                                            onChange={(e) => {
                                                                                const budgetKey = `${plan.PlanId}-${sectionKey}-${serviceCode}`;
                                                                                const updatedBudgets = {
                                                                                    ...approvedBudgets,
                                                                                    [budgetKey]: e.target.value
                                                                                };
                                                                                setApprovedBudgets(updatedBudgets);
                                                                                
                                                                                if (isGuestMode) {
                                                                                    saveToLocalStorage(GUEST_BUDGETS_KEY, updatedBudgets);
                                                                                }
                                                                            }}
                                                                            onBlur={() => setEditingCell({ sectionKey: null, rowId: null, field: null })}
                                                                            onKeyDown={(e) => { 
                                                                                if (e.key === "Enter" || e.key === "Escape") 
                                                                                    setEditingCell({ sectionKey: null, rowId: null, field: null }); 
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <span className="text-sm font-semibold cursor-pointer hover:bg-gray-100 px-3 py-1 rounded">
                                                                            {approvedBudgets[`${plan.PlanId}-${sectionKey}-${serviceCode}`] 
                                                                                ? `$${Number(approvedBudgets[`${plan.PlanId}-${sectionKey}-${serviceCode}`]).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                                                                                : <span className="text-gray-400 italic">Enter budget</span>
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                
                                                                <div className="text-center">
                                                                    <div className="text-xs text-gray-500 mb-1">Allocated Budget Total</div>
                                                                    <span className="text-sm font-semibold">
                                                                        ${serviceRows.reduce((sum, r) => sum + (Number(r.allocatedBudget || 0)), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </span>
                                                                </div>
                                                                
                                                                <div className="text-center">
                                                                    <div className="text-xs text-gray-500 mb-1">Variance Analysis</div>
                                                                    {(() => {
                                                                        const actualTotal = serviceRows.reduce((sum, r) => sum + (Number(r.pricingUnit || 0) * Number(r.frequency || 0)), 0);
                                                                        const allocatedTotal = serviceRows.reduce((sum, r) => sum + (Number(r.allocatedBudget || 0)), 0);
                                                                        const approvedBudget = Number(approvedBudgets[`${plan.PlanId}-${sectionKey}-${serviceCode}`] || 0);
                                                                        if (approvedBudget > 0) {
                                                                            const actualDiff = actualTotal - approvedBudget;
                                                                            const allocatedDiff = allocatedTotal - approvedBudget;
                                                                            return (
                                                                                <div className="flex flex-col gap-1">
                                                                                    <div className="text-xs">
                                                                                        <span className="text-gray-600">Actual vs Approved: </span>
                                                                                        <span className={`font-semibold ${Math.abs(actualDiff) < 0.01 ? 'text-blue-600' : actualDiff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                                                            {Math.abs(actualDiff) < 0.01 ? '—' : `${actualDiff > 0 ? '+' : ''}${actualDiff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="text-xs">
                                                                                        <span className="text-gray-600">Allocated vs Approved: </span>
                                                                                        <span className={`font-semibold ${Math.abs(allocatedDiff) < 0.01 ? 'text-blue-600' : allocatedDiff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                                                            {Math.abs(allocatedDiff) < 0.01 ? '—' : `${allocatedDiff > 0 ? '+' : ''}${allocatedDiff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        return <span className="text-gray-400">—</span>;
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ));
                                })()}
                                            {/* Section total */}
                                            <tr className="bg-[#d1e7f5] text-gray-800 border-b border-[#b5d6c2] font-bold 2xl:text-2xl">
                                                <td colSpan={9} className="p-2 text-center">
                                                    Section Total
                                                </td>
                                                <td className="p-2 text-center">
                                                    {rows.reduce((sum, r) => sum + Number(r.frequency || 0), 0).toFixed(0)}
                                                </td>
                                                <td className="p-2 text-center">
                                                    ${rows.reduce((sum, r) => sum + (Number(r.pricingUnit || 0) * Number(r.frequency || 0)), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </td>
                                                <td className="p-2"></td>
                                                <td className="p-2"></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    );
                })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Individual Plan Export Functions
    // Export wrapper functions that handle plan locking for guest users
    const handleExportToExcel = (plan, planApiSections) => {
        exportPlanToExcel(plan, planApiSections);
        
        // Plan lock removed — do not lock after export
    };
    
    const handleExportToPDF = (plan, planApiSections) => {
        exportPlanToPDF(plan, planApiSections);
        
        // Plan lock removed — do not lock after export
    };

    const exportPlanToExcel = async (plan, planApiSections) => {
        const workbook = new ExcelJS.Workbook();
        const planDisplayName = plan.Name || plan.planName || `Plan ${plan.PlanId}`;
        const sheet = workbook.addWorksheet(planDisplayName);

        // Plan header info
        sheet.addRow([planDisplayName]);
        sheet.mergeCells(`A1:K1`);
    sheet.addRow([`Period: ${formatDateUS(plan.start_date)} - ${formatDateUS(plan.end_date)}`]);
        sheet.mergeCells(`A2:K2`);
        sheet.addRow([`Status: ${plan.IsActive ? 'Active' : 'Inactive'} | Year: ${plan.Year}`]);
        sheet.mergeCells(`A3:K3`);
        sheet.addRow([]);

        // const headerColumns = [
        //     "Service Code",
        //     "Provider", 
        //     "Service Description",
        //     "IPP Goal",
        //     "Base Pay",
        //     "Payment Model",
        //     "Pricing Unit",
        //     "Annual Frequency",
        //     "Total Annual Cost",
        // ];

        // Map plan_type to section titles with service code ranges
        const sectionTitles = {
            'livingarrangements': 'Living Arrangements (310–330)',
            'employmentandcommunityparticipation': 'Employment and Community Participation (331–355)', 
            'healthandsafety': 'Health and Safety (356–399)'
        };

        let grandTotal = 0;
        const sectionSubtotalRows = [];
        const sectionTitleRows = [];

        Object.entries(sectionTitles).forEach(([sectionKey, sectionTitle]) => {
            const sectionData = planApiSections[sectionKey] || [];
            
            // Section title
            sheet.addRow([sectionTitle]);
            const titleRowNum = sheet.lastRow.number;
            sectionTitleRows.push(titleRowNum);
            sheet.mergeCells(`A${titleRowNum}:K${titleRowNum}`);
            
            // Section headers
            const headerColumns = [
                "Service Code",
                "Provider",
                "Not EVV",
                "Service Description",
                "IPP Goal",
                "Base Pay",
                "Burden Rate",
                "Pricing Unit",
                "Annual Frequency",
                "Total Annual Cost",
                "Allocated Budget",
            ];
            // Add header row for this section so Excel has column headers
            const headerRow = sheet.addRow(headerColumns);
           // const headerRowNum = headerRow.number;
            // Apply basic header styling immediately so it appears above the data
            headerRow.height = 22;
            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F0CCB6" } };
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
                cell.border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" },
                };
            });

                if (sectionData.length > 0) {
                // Group by serviceCode for subtotals
                const groups = {};
                sectionData.forEach((r) => {
                    const code = r.serviceCode || r.ServiceCode || 'Unknown';
                    if (!groups[code]) groups[code] = [];
                    groups[code].push(r);
                });

                let sectionTotal = 0;

                Object.keys(groups).forEach((code) => {
                    groups[code].forEach((r) => {
                        const annualCost = Number(r.frequency || r.Frequency || 0) * Number(r.pricingUnit || r.UnitCost || 0);
                        sheet.addRow([
                            r.serviceCode || r.ServiceCode,
                            r.provider || r.Provider,
                            r.liveIn || r.LiveIn ? 'Yes' : 'No',
                            r.description || r.Description,
                            r.ippGoal || r.IppGoal,
                            r.basePay || r.BasePay,
                            r.burdenRate !== undefined ? `${r.burdenRate}%` : (r.PaymentModel ? `${r.PaymentModel}%` : ''),
                            r.pricingUnit || r.UnitCost,
                            r.frequency || r.Frequency,
                            annualCost,
                            r.allocatedBudget || r.AllocatedBudget || ''
                        ]);
                        sectionTotal += annualCost;
                    });

                    // Add subtotal for each service code group (always show like main export)
                    const subtotal = groups[code].reduce((sum, r) => 
                        sum + (Number(r.frequency || r.Frequency || 0) * Number(r.pricingUnit || r.UnitCost || 0)), 0
                    );
                    const allocatedSubtotal = groups[code].reduce((sum, r) => 
                        sum + (Number(r.allocatedBudget || r.AllocatedBudget || 0)), 0
                    );
                    sheet.addRow([
                        "", "", "", "", "", "", "", "", `Subtotal ${code}`, subtotal, ""
                    ]);
                    
                    // Add budget comparison row for this service code
                    const budgetKey = `${plan.PlanId}-${sectionKey}-${code}`;
                    const approvedBudget = Number(approvedBudgets[budgetKey] || 0);
                    const actualDiff = subtotal - approvedBudget;
                    const allocatedDiff = allocatedSubtotal - approvedBudget;
                    
                    const budgetRow = sheet.addRow([
                        "", "", "", `Approved Budget for ${code}:`, "", "",
                        `Actual: $${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        `Approved: ${approvedBudget > 0 ? '$' + approvedBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'Not Set'}`,
                        `Allocated: $${allocatedSubtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        approvedBudget > 0 ? `Variance: ${actualDiff >= 0 ? '+' : ''}${actualDiff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
                        approvedBudget > 0 ? `Alloc Var: ${allocatedDiff >= 0 ? '+' : ''}${allocatedDiff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''
                    ]);
                    budgetRow.height = 20;
                    budgetRow.eachCell((cell) => {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "F0F8FF" }
                        };
                        cell.font = { italic: true };
                        cell.alignment = { horizontal: "left", vertical: "middle" };
                    });
                });

                // Add section subtotal
                const sectionSubtotalRow = sheet.addRow([
                    "", "", "", "", "", "", "", "", `Subtotal (${sectionTitle.split(' (')[0]})`, sectionTotal, ""
                ]);
                sectionSubtotalRows.push(sectionSubtotalRow.number);
                grandTotal += sectionTotal;
            } else {
                sheet.addRow(["No data available for this section"]);
                sheet.mergeCells(`A${sheet.lastRow.number}:K${sheet.lastRow.number}`);
            }

            sheet.addRow([]);
        });

        // Grand total
        const grandTotalRow = sheet.addRow([
            "Grand Total", "", "", "", "", "", "", "", "", grandTotal, ""
        ]);
        sheet.mergeCells(`A${grandTotalRow.number}:I${grandTotalRow.number}`);

        /** ---- Styling (Same as main export) ---- **/
        
        // Plan header styling
        [1, 2, 3].forEach((rowNum) => {
            const row = sheet.getRow(rowNum);
            row.height = 25;
            row.eachCell((cell) => {
                cell.font = { bold: true, size: rowNum === 1 ? 16 : 12 };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "E6F3FF" },
                };
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            });
        });

        // Section titles
        sectionTitleRows.forEach((rowNum) => {
            const row = sheet.getRow(rowNum);
            row.height = 30;
            row.eachCell((cell) => {
                cell.font = { bold: true, size: 14, color: { argb: "000000" } };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "c8e0e8" },
                };
                cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            });
        });

        // Column headers (find all header rows)
        sheet.eachRow((row, rowNum) => {
            if (row.getCell(1).value === "Service Code") {
                row.height = 25;
                row.eachCell((cell) => {
                    cell.font = { bold: true };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "f0ccb6" },
                    };
                    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
                    cell.border = {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                        left: { style: "thin" },
                        right: { style: "thin" },
                    };
                });
            }
        });

        // Borders for all data rows
        sheet.eachRow((row, rowNum) => {
            if (rowNum > 4) { // Skip header rows
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: "thin" },
                        bottom: { style: "thin" },
                        left: { style: "thin" },
                        right: { style: "thin" },
                    };
                    cell.alignment = { wrapText: true, vertical: "middle" };
                });
            }
        });

        // Section subtotals (grey)
        sectionSubtotalRows.forEach((rowNum) => {
            const row = sheet.getRow(rowNum);
            row.height = 25;
            row.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "D9D9D9" },
                };
                cell.alignment = { horizontal: "right", vertical: "middle" };
            });
        });

        // Grand total (dark fill, white text)
        grandTotalRow.height = 28;
        grandTotalRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFF" } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "444444" },
            };
            cell.alignment = { horizontal: "right", vertical: "middle" };
        });

        // Currency formatting
        ["F", "H", "J", "K"].forEach((col) => {
            sheet.getColumn(col).numFmt = '"$"#,##0.00;[Red]-"$"#,##0.00';
        });

        // Set custom widths for each column (all 11 columns)
        sheet.columns = [
            { key: "serviceCode", width: 15 },      // A
            { key: "provider", width: 20 },         // B
            { key: "liveIn", width: 10 },           // C
            { key: "description", width: 35 },      // D
            { key: "ippGoal", width: 30 },          // E
            { key: "basePay", width: 15 },          // F
            { key: "burdenRate", width: 18 },       // G
            { key: "pricingUnit", width: 15 },      // H
            { key: "frequency", width: 20 },        // I
            { key: "annualCost", width: 18 },       // J
            { key: "allocatedBudget", width: 18 },  // K
        ];
        
        // Save the file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer]);
        saveAs(blob, `spending-plan-${plan.PlanId}.xlsx`);
    };

    const exportPlanToPDF = (plan, planApiSections) => {
        const doc = new jsPDF();
        
        // Plan header
        const planDisplayName = plan.Name || plan.planName || `Plan #${plan.PlanId}`;
        doc.setFontSize(16);
        doc.text(planDisplayName, 20, 20);
        
        doc.setFontSize(12);
    doc.text(`Period: ${formatDateUS(plan.start_date)} - ${formatDateUS(plan.end_date)}`, 20, 35);
        doc.text(`Status: ${plan.IsActive ? 'Active' : 'Inactive'} | Year: ${plan.Year}`, 20, 45);

        let startY = 60;
        let grandTotal = 0;

        const sectionTitles = {
            'livingarrangements': 'Living Arrangements (310–330)',
            'employmentandcommunityparticipation': 'Employment and Community Participation (331–355)', 
            'healthandsafety': 'Health and Safety (356–399)'
        };

        Object.entries(sectionTitles).forEach(([sectionKey, sectionTitle]) => {
            const sectionData = planApiSections[sectionKey] || [];
            
            // Add section title
            doc.setFontSize(14);
            doc.text(sectionTitle, 20, startY);
            startY += 15;

            if (sectionData.length > 0) {
                // Group by service code
                const groups = {};
                sectionData.forEach((r) => {
                    const code = r.serviceCode || r.ServiceCode || 'Unknown';
                    if (!groups[code]) groups[code] = [];
                    groups[code].push(r);
                });

                const body = [];
                let serviceCodeTotals = {}; // Track totals per service code
                let serviceCodeAllocated = {}; // Track allocated per service code
                
                Object.keys(groups).forEach((code) => {
                    let codeTotal = 0;
                    let codeAllocated = 0;
                    
                    groups[code].forEach((r) => {
                        const annualCost = Number(r.frequency || r.Frequency || 0) * Number(r.pricingUnit || r.UnitCost || 0);
                        const allocated = Number(r.allocatedBudget || r.AllocatedBudget || 0);
                        body.push([
                            r.serviceCode || r.ServiceCode,
                            r.provider || r.Provider,
                            r.liveIn || r.LiveIn ? 'Yes' : 'No',
                            r.description || r.Description,
                            r.ippGoal || r.IppGoal,
                            r.burdenRate !== undefined ? `${r.burdenRate}%` : (r.PaymentModel ? `${r.PaymentModel}%` : ''),
                            `$${(r.pricingUnit || r.UnitCost || 0).toLocaleString()}`,
                            r.frequency || r.Frequency,
                            `$${annualCost.toLocaleString()}`,
                            allocated ? `$${allocated.toLocaleString()}` : ''
                        ]);
                        grandTotal += annualCost;
                        codeTotal += annualCost;
                        codeAllocated += allocated;
                    });
                    
                    serviceCodeTotals[code] = codeTotal;
                    serviceCodeAllocated[code] = codeAllocated;

                    if (groups[code].length > 1) {
                        body.push([{
                            content: `Subtotal ${code}: $${codeTotal.toLocaleString()}`,
                            colSpan: 10,
                            styles: { halign: "right", fontStyle: "bold", fillColor: [230, 230, 230] }
                        }]);
                    }
                    
                    // Add budget comparison row
                    const budgetKey = `${plan.PlanId}-${sectionKey}-${code}`;
                    const approvedBudget = Number(approvedBudgets[budgetKey] || 0);
                    if (approvedBudget > 0) {
                        const actualDiff = codeTotal - approvedBudget;
                        const allocatedDiff = codeAllocated - approvedBudget;
                        body.push([{
                            content: `Budget for ${code} | Actual: $${codeTotal.toLocaleString()} | Approved: $${approvedBudget.toLocaleString()} | Allocated: $${codeAllocated.toLocaleString()} | Actual Var: ${actualDiff >= 0 ? '+' : ''}$${Math.abs(actualDiff).toLocaleString()} | Alloc Var: ${allocatedDiff >= 0 ? '+' : ''}$${Math.abs(allocatedDiff).toLocaleString()}`,
                            colSpan: 10,
                            styles: { halign: "left", fontStyle: "italic", fillColor: [240, 248, 255], fontSize: 7 }
                        }]);
                    }
                });

                if (body.length > 0) {
                    autoTable(doc, {
                        startY: startY,
                        head: [["Service Code", "Provider", "Not EVV", "Description", "IPP Goal", "Burden Rate", "Unit Cost", "Frequency", "Annual Cost", "Allocated Budget"]],
                        body: body,
                        theme: 'grid',
                        headStyles: { fillColor: [41, 128, 185] },
                        styles: { fontSize: 8 }
                    });
                    startY = doc.lastAutoTable.finalY + 10;
                }
            } else {
                doc.setFontSize(10);
                doc.text("No data available for this section", 20, startY);
                startY += 15;
            }

            startY += 5;
        });

        // Grand total
        doc.setFontSize(14);
        doc.text(`Grand Total: $${grandTotal.toLocaleString()}`, 20, startY);

        doc.save(`spending-plan-${plan.PlanId}.pdf`);
    };


    // Helper to get section label for dropdown
    const getServiceCodeLabel = (sectionKey) => {
        if (sectionKey === "livingarrangements" || sectionKey === "livingArrangements") return "Living arrangements: (310-330)";
        if (sectionKey === "employmentandcommunityparticipation" || sectionKey === "employmentCommunityParticipation") return "Employment and Community Participation (331-355)";
        if (sectionKey === "healthandsafety" || sectionKey === "healthAndSafety") return "Health and Safety (356 - 399)";
        return "";
    };

    // Helper to filter service codes by section
    const getFilteredServiceCodes = (sectionKey) => {
        console.log('Getting filtered service codes for section:', sectionKey);
        console.log('Available service code options:', serviceCodeOptions);
        
        if (!Array.isArray(serviceCodeOptions)) {
            console.log('Service code options is not an array');
            return [];
        }
        
        // Since the API returns ServiceTypeID and Name, use them directly
        console.log('Using service codes with ServiceTypeID and Name format');
        
        // Match the actual section keys from API data (all lowercase)
        if (sectionKey === "livingarrangements" || sectionKey === "livingArrangements") {
            const filtered = serviceCodeOptions.filter(opt => {
                if (!opt.Name) return false;
                const code = parseInt(opt.Name.split(" ")[0]);
                return !isNaN(code) && code >= 310 && code <= 330;
            });
            // Always include "320 - Community Living Supports" if not present
            if (!filtered.some(opt => opt.Name && opt.Name.includes("320"))) {
                filtered.push({
                    ServiceTypeID: "320",
                    Name: "320 - Community Living Supports"
                });
            }
            console.log('Filtered living arrangements:', filtered);
            return filtered;
        }
        
        if (sectionKey === "employmentandcommunityparticipation" || sectionKey === "employmentCommunityParticipation") {
            const filtered = serviceCodeOptions.filter(opt => {
                if (!opt.Name) return false;
                const code = parseInt(opt.Name.split(" ")[0]);
                return !isNaN(code) && code >= 331 && code <= 355;
            });
            console.log('Filtered employment options:', filtered);
            return filtered;
        }
        
        if (sectionKey === "healthandsafety" || sectionKey === "healthAndSafety") {
            const filtered = serviceCodeOptions.filter(opt => {
                if (!opt.Name) return false;
                const code = parseInt(opt.Name.split(" ")[0]);
                return !isNaN(code) && code >= 356 && code <= 399;
            });
            console.log('Filtered health and safety options:', filtered);
            return filtered;
        }
        
        return [];
    };

    return (
        <div className="px-8 2xl:px-20">
            {/* Toast Notifications */}
            <Toaster 
                position="bottom-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        iconTheme: {
                            primary: '#4ade80',
                            secondary: '#fff',
                        },
                    },
                    error: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#ef4444',
                            secondary: '#fff',
                        },
                    },
                }}
            />
            {/* Loading State */}
            {isLoadingData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center ">
                        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Spending Plans</h3>
                        <p className="text-gray-600">
                            {isGuestMode 
                                ? "Setting up your guest workspace..." 
                                : "Fetching your spending plans and data..."
                            }
                        </p>
                        <div className="mt-4 text-sm text-gray-500">
                            Please wait while we prepare your dashboard
                        </div>
                    </div>
                </div>
            )}

            {/* Header title - only show when not loading */}
            {!isLoadingData && (
                <>
                    <h1 className="text-2xl 2xl:text-4xl font-bold mb-3 mt-10 2xl:mt-20">Self-Determination Spending Plan</h1>
                    <p className="text-gray-500 mb-6 2xl:text-lg">Manage your service allocations and budget planning with an intuitive interface</p>
                </>
            )}

            {/* Main Content - only show when not loading */}
            {!isLoadingData && (
                <>
                    {/* Guest mode info removed (plan-locking disabled) */}


            {/* Toolbar: search + Create Plan */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-6">
                {spendingPlans.length > 0 ? (
                    <div className="flex-1">
                        <div className="relative">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search services, providers..."
                                className="w-full border rounded-lg py-2 pl-10 pr-10"
                            />
                            <span className="absolute left-3 top-2.5 text-gray-400">
                                {/* Modern grey search icon SVG */}
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                                </svg>
                            </span>
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                    title="Clear search"
                                >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex-1">
                        {/* Show create plan button when no plans exist */}
                        {isGuestMode && (
                            <div className="text-center">
                                <button
                                    onClick={() => setShowAddPlanModal(true)}
                                    className="bg-primary-dark text-white px-6 py-3 rounded-lg shadow hover:shadow-lg transition-all duration-200 font-medium"
                                >
                                    <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Create Your First Spending Plan
                                </button>
                            </div>
                        )}
                    </div>
                )}
                {/* <div className="ml-auto relative">
                    <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        className="bg-primary-dark text-white px-4 py-2 rounded-lg shadow"
                    >
                        <img src="/print.svg" alt="Print" className="inline w-5 h-5 mr-2" />
                        Export
                    </button>
                    {dropdownOpen && (
                        <div className="absolute right-0 top-full mt-2 w-44 bg-white border rounded shadow-lg z-10">
                            <button onClick={exportToExcel} className="w-full text-left px-4 py-2 hover:bg-gray-100">Save as Excel</button>
                            <button onClick={exportToPDF} className="w-full text-left px-4 py-2 hover:bg-gray-100">Save as PDF</button>
                        </div>
                    )}
                </div> */}
            </div>

            {/* Tab Navigation */}
            <div className="mb-8">
                <div className="border-b border-gray-200 shadow-sm">
                    <div className="flex justify-between items-center">
                        <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab("previous")}
                            className={`py-3 px-2 border-b-2 font-medium text-sm 2xl:text-lg transition-all duration-200 ${
                                activeTab === "previous"
                                    ? "border-[#2A3F54] text-[#2A3F54] bg-gradient-to-t from-[#2A3F54]/5 to-transparent"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                        >
                            Previous Plans
                        </button>
                        <button
                            onClick={() => setActiveTab("current")}
                            className={`py-3 px-2 border-b-2 font-medium text-sm 2xl:text-lg transition-all duration-200 ${
                                activeTab === "current"
                                    ? "border-[#2A3F54] text-[#2A3F54] bg-gradient-to-t from-[#2A3F54]/5 to-transparent"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                        >
                            Current Plans
                        </button>
                        <button
                            onClick={() => setActiveTab("future")}
                            className={`py-3 px-2 border-b-2 font-medium text-sm 2xl:text-lg transition-all duration-200 ${
                                activeTab === "future"
                                    ? "border-[#2A3F54] text-[#2A3F54] bg-gradient-to-t from-[#2A3F54]/5 to-transparent"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                        >
                            Future Plans
                        </button>
                    </nav>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowAddPlanModal(true)}
                            className="bg-primary-dark text-white px-4 py-2 rounded-lg shadow hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add New Plan
                        </button>
                        {isUserLoggedIn() && (
                            <button
                                onClick={handleLogout}
                                className="bg-gray-200 text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-300 transition-all duration-150"
                            >
                                Logout
                            </button>
                        )}
                        {/* Small Login button in header for guest users */}
                        {isGuestMode && (
                            <button
                                onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                                className="bg-white text-primary-dark border border-primary-dark px-3 py-2 rounded-lg shadow-sm hover:bg-primary-dark hover:text-white transition-colors duration-150 text-sm"
                                title="Login"
                            >
                                Login
                            </button>
                        )}
                    </div>
                </div>
                </div>
            </div>

            {/* Tab Content */}
                {activeTab === "previous" && (
                <div>
                    {/* API Data Tables - Lines from previous plans grouped by plan_type */}
                    {renderPlanLinesData('previous') || (
                        <div className="text-center py-16">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Previous Plans</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">Previous spending plans will appear here once they are archived or completed.</p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === "current" && (
                <div>
                    {/* Show loading state while data is being fetched */}
                    {isLoadingData ? (
                        <div className="text-center py-16">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full mb-4">
                                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Current Plans</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">Fetching your active spending plans...</p>
                        </div>
                    ) : (
                        /* API Data Tables with Interactive Functionality */
                        renderPlanLinesData('current') || (
                            <div className="text-center py-16">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-100 to-blue-100 rounded-full mb-4">
                                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No Current Plans</h3>
                                <p className="text-gray-500 max-w-sm mx-auto">No active spending plans found for the current date range. Create a new plan to get started.</p>
                            </div>
                        )
                    )}
                </div>
            )}

            {activeTab === "future" && (
                <div>
                    {/* API Data Tables - Lines from future plans grouped by plan_type */}
                    {renderPlanLinesData('future') || (
                        <div className="text-center py-16">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full mb-4">
                                <svg className="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Future Plans</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">Future spending plans and budget projections will appear here when scheduled.</p>
                        </div>
                    )}
                </div>
            )}
                </>
            )}

            {/* Add Plan Modal */}
            {showAddPlanModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Add New Spending Plan</h3>
                            <button
                                onClick={() => setShowAddPlanModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <form onSubmit={(e) => { e.preventDefault(); handleAddPlan(); }} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name</label>
                                <input
                                    type="text"
                                    value={newPlanForm.planName}
                                    onChange={(e) => handlePlanFormChange('planName', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                    placeholder="Enter plan name"
                                    required
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                    {/* <input
                                        type="date"
                                        value={newPlanForm.startDate || ''}
                                        onChange={(e) => handlePlanFormChange('startDate', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                    /> */}
                                    <DatePicker
                                        selected={newPlanForm.startDate ? new Date(newPlanForm.startDate) : null}
                                        onChange={(date) => handlePlanFormChange('startDate', date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                        />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                    {/* <input
                                        type="date"
                                        value={newPlanForm.endDate || ''}
                                        onChange={(e) => handlePlanFormChange('endDate', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                    /> */}
                                     <DatePicker
                                        selected={newPlanForm.endDate ? new Date(newPlanForm.endDate) : null}
                                        onChange={(date) => handlePlanFormChange('endDate', date)}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                        />
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAddPlanModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isAddingPlan}
                                    className={`flex-1 px-4 py-2 bg-primary-dark text-white rounded-lg transition-all duration-200 ${
                                        isAddingPlan 
                                            ? 'opacity-50 cursor-not-allowed' 
                                            : 'hover:shadow-lg'
                                    }`}
                                >
                                    {isAddingPlan ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Creating...
                                        </div>
                                    ) : (
                                        'Create Plan'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Authentication Modal */}
            {showAuthModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 h-[80%] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold m-auto text-gray-900 py-3">
                                {authMode === 'login' ? 'Login to Save Plan' : 'Register to Save Plan'}
                            </h3>
                            <button
                                onClick={() => setShowAuthModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <form onSubmit={handleAuth} className="space-y-4">
                            <fieldset disabled={isAuthenticating} aria-busy={isAuthenticating}>
                            {authMode === 'register' && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                            <input
                                                type="text"
                                                value={authForm.firstName}
                                                onChange={(e) => setAuthForm({...authForm, firstName: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                            <input
                                                type="text"
                                                value={authForm.lastName}
                                                onChange={(e) => setAuthForm({...authForm, lastName: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                                required
                                            />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                                        {/* <input
                                            type="date"
                                            value={authForm.dateOfBirth || ''}
                                            onChange={(e) => setAuthForm({...authForm, dateOfBirth: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                            required
                                        /> */}
                                        <DatePicker
                                        selected={authForm.dateOfBirth ? new Date(authForm.dateOfBirth) : null}
                                        onChange={(date) => setAuthForm({ ...authForm, dateOfBirth: date })}
                                        dateFormat="MM/dd/yyyy"
                                        placeholderText="MM/DD/YYYY"
                                        required
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        />
                                    </div>
                                </>
                            )}
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={authForm.email}
                                    onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                <input
                                    type="password"
                                    value={authForm.password}
                                    onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                    required
                                />
                            </div>
                            
                            {authMode === 'register' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                                    <input
                                        type="password"
                                        value={authForm.confirmPassword}
                                        onChange={(e) => setAuthForm({...authForm, confirmPassword: e.target.value})}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#38b2ac] focus:border-transparent"
                                        required
                                    />
                                </div>
                            )}
                            {authMode === 'register' && (
                                <div className="pt-2">
                                    {/* <h4 className="text-sm font-medium text-gray-800 mb-2">Contact Details</h4> */}
                                    {/* Contact first/last name fields removed — patient first/last already captured in the top of the form */}

                                    <div className="grid grid-cols-2 gap-4 mt-3">
                                        {/* <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Contact Email</label>
                                            <input
                                                type="email"
                                                value={authForm.contactEmail}
                                                onChange={(e) => setAuthForm({...authForm, contactEmail: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div> */}
                                        {/* <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Mobile Phone</label>
                                            <input
                                                type="tel"
                                                value={authForm.contactMobilePhone}
                                                onChange={(e) => setAuthForm({...authForm, contactMobilePhone: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div> */}
                                    </div>

                                    {/* <div className="mt-3">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                                        <input
                                            type="text"
                                            value={authForm.contactAddress1}
                                            onChange={(e) => setAuthForm({...authForm, contactAddress1: e.target.value})}
                                            className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                        />
                                    </div> */}

                                    {/* <div className="grid grid-cols-3 gap-4 mt-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                                            <input
                                                type="text"
                                                value={authForm.contactCity}
                                                onChange={(e) => setAuthForm({...authForm, contactCity: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                                            <input
                                                type="text"
                                                value={authForm.contactState}
                                                onChange={(e) => setAuthForm({...authForm, contactState: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Postal Code</label>
                                            <input
                                                type="text"
                                                value={authForm.contactPostalCode}
                                                onChange={(e) => setAuthForm({...authForm, contactPostalCode: e.target.value})}
                                                className="w-full border border-gray-300 rounded-lg px-2 py-1"
                                            />
                                        </div>
                                    </div> */}

                                    {/* <div className="mt-3">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Relationship (ID)</label>
                                        <input
                                            type="number"
                                            value={authForm.contactRelationshipId}
                                            onChange={(e) => setAuthForm({...authForm, contactRelationshipId: Number(e.target.value)})}
                                            className="w-32 border border-gray-300 rounded-lg px-2 py-1"
                                        />
                                    </div> */}
                                </div>
                            )}
                            
                            <div className="flex gap-3 justify-center items-center pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowAuthModal(false)}
                                    className={`px-6 py-2 border border-gray-300 text-gray-700 rounded-lg ${(isAuthenticating || !isBusinessLoginComplete) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                    disabled={isAuthenticating || !isBusinessLoginComplete}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isAuthenticating || !isBusinessLoginComplete}
                                    className={`px-6 py-2 bg-primary-dark text-white rounded-lg transition-all duration-200 ${(isAuthenticating || !isBusinessLoginComplete) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'}`}
                                >
                                    {!isBusinessLoginComplete ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Initializing...
                                        </div>
                                    ) : isAuthenticating ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            {authMode === 'login' ? 'Logging in...' : 'Registering...'}
                                        </div>
                                    ) : (
                                        (authMode === 'login' ? 'Login' : 'Register')
                                    )}
                                </button>
                            </div>
                            </fieldset>
                            
                            <div className="text-center pt-2">
                                <button
                                    type="button"
                                    onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                                    className="text-sm text-[#2A3F54] hover:underline"
                                >
                                    {authMode === 'login' 
                                        ? "Don't have an account? Register" 
                                        : "Already have an account? Login"
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">{confirmModalData.title}</h3>
                        <p className="text-gray-600 mb-6 whitespace-pre-line">{confirmModalData.message}</p>
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {confirmModalData.cancelText}
                            </button>
                            <button
                                onClick={confirmModalData.onConfirm}
                                className="flex-1 bg-danger-dark text-white px-4 py-2 rounded-lg shadow hover:from-red-700 hover:to-red-800 transition-all duration-200 flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {confirmModalData.confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


















